'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

/* global */

/**
 * @typedef {Object} CONSTSCODES
 */

/**
 * Continue
 *
 * This means that the server has received the request headers, and that the client should proceed to send the request body (in the case of a request for which a body needs to be sent, for example, a POST request). If the request body is large, sending it to a server when a request has already been rejected based upon inappropriate headers is inefficient. To have a server check if the request could be accepted based on the request's headers alone, a client must send Expect: 100-continue as a header in its initial request and check if a 100 Continue status code is received in response before continuing (or receive 417 Expectation Failed and not continue).[6]
 * @type {Number}
 */
const CONTINUE = 100;

/**
 * Switching Protocols
 *
 * This means the requester has asked the server to switch protocols and the server is acknowledging that it will do so.[7]
 * @type {Number}
 */
const SWITCHING_PROTOCOLS = 101;

/**
 * Processing (WebDAV, RFC 2518)
 *
 * As a WebDAV request may contain many sub-requests involving file operations, it may take a long time to complete the request. This code indicates that the server has received and is processing the request, but no response is available yet.[8] This prevents the client from timing out and assuming the request was lost.
 * @type {Number}
 */
const PROCESSING = 102;

/**
 * OK
 *
 * Standard response for successful HTTP requests. The actual response will depend on the request method used. In a GET request, the response will contain an entity corresponding to the requested resource. In a POST request, the response will contain an entity describing or containing the result of the action.[10]
 * @type {Number}
 */
const OK = 200;

/**
 * Created
 *
 * The request has been fulfilled and resulted in a new resource being created.[11]
 * @type {Number}
 */
const CREATED = 201;

/**
 * Accepted
 *
 * The request has been accepted for processing, but the processing has not been completed. The request might or might not eventually be acted upon, as it might be disallowed when processing actually takes place.[12]
 * @type {Number}
 */
const ACCEPTED = 202;

/**
 * Non-Authoritative Information (since HTTP/1.1)
 *
 * The server successfully processed the request, but is returning information that may be from another source.[13]
 * @type {Number}
 */
const NON_AUTHORITATIVE_INFORMATION = 203;

/**
 * No Content
 *
 * The server successfully processed the request, but is not returning any content.[14]
 * @type {Number}
 */
const NO_CONTENT = 204;

/**
 * Reset Content
 *
 * The server successfully processed the request, but is not returning any content. Unlike a 204 response, this response requires that the requester reset the document view.[15]
 * @type {Number}
 */
const RESET_CONTENT = 205;

/**
 * Partial Content (RFC 7233)
 *
 * The server is delivering only part of the resource (byte serving) due to a range header sent by the client. The range header is used by HTTP clients to enable resuming of interrupted downloads, or split a download into multiple simultaneous streams.[16]
 * @type {Number}
 */
const PARTIAL_CONTENT = 206;

/**
 * Multi-Status (WebDAV, RFC 4918)
 *
 * The message body that follows is an XML message and can contain a number of separate response codes, depending on how many sub-requests were made.[17]
 * @type {Number}
 */
const MULTI_STATUS = 207;

/**
 * Already Reported (WebDAV, RFC 5842)
 *
 * The members of a DAV binding have already been enumerated in a previous reply to this request, and are not being included again.[18]
 * @type {Number}
 */
const ALREADY_REPORTED = 208;

/**
 * IM Used (RFC 3229)
 *
 * The server has fulfilled a request for the resource, and the response is a representation of the result of one or more instance-manipulations applied to the current instance.[19]
 * @type {Number}
 */
const IM_USED = 226;

/**
 * Multiple Choices
 *
 * Indicates multiple options for the resource that the client may follow. It, for instance, could be used to present different format options for video, list files with different extensions, or word sense disambiguation.[22]
 * @type {Number}
 */
const MULTIPLE_CHOICES = 300;

/**
 * Moved Permanently
 *
 * This and all future requests should be directed to the given URI.[23]
 * @type {Number}
 */
const MOVED_PERMANENTLY = 301;

/**
 * Found
 *
 * This is an example of industry practice contradicting the standard. The HTTP/1.0 specification (RFC 1945) required the client to perform a temporary redirect (the original describing phrase was "Moved Temporarily"),[24] but popular browsers implemented 302 with the functionality of a 303 See Other. Therefore, HTTP/1.1 added status codes 303 and 307 to distinguish between the two behaviours.[25] However, some Web applications and frameworks use the 302 status code as if it were the 303.[26]
 * @type {Number}
 */
const FOUND = 302;

/**
 * See Other (since HTTP/1.1)
 *
 * The response to the request can be found under another URI using a GET method. When received in response to a POST (or PUT/DELETE), it should be assumed that the server has received the data and the redirect should be issued with a separate GET message.[27]
 * @type {Number}
 */
const SEE_OTHER = 303;

/**
 * Not Modified (RFC 7232)
 *
 * Indicates that the resource has not been modified since the version specified by the request headers If-Modified-Since or If-None-Match. This means that there is no need to retransmit the resource, since the client still has a previously-downloaded copy.[28]
 * @type {Number}
 */
const NOT_MODIFIED = 304;

/**
 * Use Proxy (since HTTP/1.1)
 *
 * The requested resource is only available through a proxy, whose address is provided in the response. Many HTTP clients (such as Mozilla[29] and Internet Explorer) do not correctly handle responses with this status code, primarily for security reasons.[30]
 * @type {Number}
 */
const USE_PROXY = 305;

/**
 * Switch Proxy
 *
 * No longer used. Originally meant "Subsequent requests should use the specified proxy."[31]
 * @type {Number}
 */
const SWITCH_PROXY = 306;

/**
 * Temporary Redirect (since HTTP/1.1)
 *
 * In this case, the request should be repeated with another URI, however, future requests should still use the original URI. In contrast to how 302 was historically implemented, the request method is not allowed to be changed when reissuing the original request. For instance, a POST request should be repeated using another POST request.[32]
 * @type {Number}
 */
const TEMPORARY_REDIRECT = 307;

/**
 * Permanent Redirect (RFC 7538)
 *
 * The request, and all future requests should be repeated using another URI. 307 and 308 (as proposed) parallel the behaviours of 302 and 301, but do not allow the HTTP method to change. So, for example, submitting a form to a permanently redirected resource may continue smoothly.[33]
 * @type {Number}
 */
const PERMANENT_REDIRECT = 308;

/**
 * Resume Incomplete (Google)
 *
 * This code is used in the Resumable HTTP Requests Proposal to resume aborted PUT or POST requests.[34]
 * @type {Number}
 */
const RESUME_INCOMPLETE = 308;

/**
 * Bad Request
 *
 * The server cannot or will not process the request due to something that is perceived to be a client error (e.g., malformed request syntax, invalid request message framing, or deceptive request routing).[36]
 * @type {Number}
 */
const BAD_REQUEST = 400;

/**
 * Unauthorized (RFC 7235)
 *
 * Similar to 403 Forbidden, but specifically for use when authentication is required and has failed or has not yet been provided. The response must include a WWW-Authenticate header field containing a challenge applicable to the requested resource. See Basic access authentication and Digest access authentication.[37]
 * @type {Number}
 */
const UNAUTHORIZED = 401;

/**
 * Payment Required
 *
 * Reserved for future use. The original intention was that this code might be used as part of some form of digital cash or micropayment scheme, but that has not happened, and this code is not usually used. Google Developers API uses this status if a particular developer has exceeded the daily limit on requests.[38]
 * @type {Number}
 */
const PAYMENT_REQUIRED = 402;

/**
 * Forbidden
 *
 * The request was a valid request, but the server is refusing to respond to it. Unlike a 401 Unauthorized response, authenticating will make no difference.[39]
 * @type {Number}
 */
const FORBIDDEN = 403;

/**
 * Not Found
 *
 * The requested resource could not be found but may be available again in the future. Subsequent requests by the client are permissible.[40]
 * @type {Number}
 */
const NOT_FOUND = 404;

/**
 * Method Not Allowed
 *
 * A request was made of a resource using a request method not supported by that resource, for example, using GET on a form which requires data to be presented via POST, or using PUT on a read-only resource.
 * @type {Number}
 */
const METHOD_NOT_ALLOWED = 405;

/**
 * Not Acceptable
 *
 * The requested resource is only capable of generating content not acceptable according to the Accept headers sent in the request.[41]
 * @type {Number}
 */
const NOT_ACCEPTABLE = 406;

/**
 * Proxy Authentication Required (RFC 7235)
 *
 * The client must first authenticate itself with the proxy.[42]
 * @type {Number}
 */
const PROXY_AUTHENTICATION_REQUIRED = 407;

/**
 * Request Timeout
 *
 * The server timed out waiting for the request. According to HTTP specifications = "The client did not produce a request within the time that the server was prepared to wait. The client MAY repeat the request without modifications at any later time."[43]
 * @type {Number}
 */
const REQUEST_TIMEOUT = 408;

/**
 * Conflict
 *
 * Indicates that the request could not be processed because of conflict in the request, such as an edit conflict in the case of multiple updates.[44]
 * @type {Number}
 */
const CONFLICT = 409;

/**
 * Gone
 *
 * Indicates that the resource requested is no longer available and will not be available again. This should be used when a resource has been intentionally removed and the resource should be purged. Upon receiving a 410 status code, the client should not request the resource again in the future. Clients such as search engines should remove the resource from their indices.[45] Most use cases do not require clients and search engines to purge the resource, and a "404 Not Found" may be used instead.
 * @type {Number}
 */
const GONE = 410;

/**
 * Length Required
 *
 * The request did not specify the length of its content, which is required by the requested resource.[46]
 * @type {Number}
 */
const LENGTH_REQUIRED = 411;

/**
 * Precondition Failed (RFC 7232)
 *
 * The server does not meet one of the preconditions that the requester put on the request.[47]
 * @type {Number}
 */
const PRECONDITION_FAILED = 412;

/**
 * Payload Too Large (RFC 7231)
 *
 * The request is larger than the server is willing or able to process. Called "Request Entity Too Large" previously.[48]
 * @type {Number}
 */
const PAYLOAD_TOO_LARGE = 413;

/**
 * URI Too Long (RFC 7231)
 *
 * The URI provided was too long for the server to process. Often the result of too much data being encoded as a query-string of a GET request, in which case it should be converted to a POST request.[49] Called "Request-URI Too Long" previously.[50]
 * @type {Number}
 */
const URI_TOO_LONG = 414;

/**
 * Unsupported Media Type
 *
 * The request entity has a media type which the server or resource does not support. For example, the client uploads an image as image/svg+xml, but the server requires that images use a different format.
 * @type {Number}
 */
const UNSUPPORTED_MEDIA_TYPE = 415;

/**
 * Range Not Satisfiable (RFC 7233)
 *
 * The client has asked for a portion of the file (byte serving), but the server cannot supply that portion. For example, if the client asked for a part of the file that lies beyond the end of the file.[51] Called "Requested Range Not Satisfiable" previously.[52]
 * @type {Number}
 */
const RANGE_NOT_SATISFIABLE = 416;

/**
 * Expectation Failed
 *
 * The server cannot meet the requirements of the Expect request-header field.[53]
 * @type {Number}
 */
const EXPECTATION_FAILED = 417;

/**
 * I'm a teapot (RFC 2324)
 *
 * This code was defined in 1998 as one of the traditional IETF April Fools' jokes, in RFC 2324, Hyper Text Coffee Pot Control Protocol, and is not expected to be implemented by actual HTTP servers. The RFC specifies this code should be returned by tea pots requested to brew coffee.[54] This HTTP status is used as an easter egg in some websites, including Google.com.[55]
 * @type {Number}
 */
const IM_A_TEAPOT = 418;

/**
 * Authentication Timeout (not in RFC 2616)
 *
 * Not a part of the HTTP standard, 419 Authentication Timeout denotes that previously valid authentication has expired. It is used as an alternative to 401 Unauthorized in order to differentiate from otherwise authenticated clients being denied access to specific server resources.[citation needed][56]
 * @type {Number}
 */
const AUTHENTICATION_TIMEOUT = 419;

/**
 * Method Failure (Spring Framework)
 *
 * Not part of the HTTP standard, but defined by Spring in the HttpStatus class to be used when a method failed. This status code is deprecated by Spring.[57]
 * @type {Number}
 */
const METHOD_FAILURE = 420;

/**
 * Enhance Your Calm (Twitter)
 *
 * Not part of the HTTP standard, but returned by version 1 of the Twitter Search and Trends API when the client is being rate limited.[58] Other services may wish to implement the 429 Too Many Requests response code instead.
 * @type {Number}
 */
const ENHANCE_YOUR_CALM = 420;

/**
 * Misdirected Request (RFC 7540)
 *
 * The request was directed at a server that is not able to produce a response (for example because a connection reuse).[59]
 * @type {Number}
 */
const MISDIRECTED_REQUEST = 421;

/**
 * Unprocessable Entity (WebDAV, RFC 4918)
 *
 * The request was well-formed but was unable to be followed due to semantic errors.[17]
 * @type {Number}
 */
const UNPROCESSABLE_ENTITY = 422;

/**
 * Locked (WebDAV, RFC 4918)
 *
 * The resource that is being accessed is locked.[17]
 * @type {Number}
 */
const LOCKED = 423;

/**
 * Failed Dependency (WebDAV, RFC 4918)
 *
 * The request failed due to failure of a previous request (e.g., a PROPPATCH).[17]
 * @type {Number}
 */
const FAILED_DEPENDENCY = 424;

/**
 * Upgrade Required
 *
 * The client should switch to a different protocol such as TLS/1.0, given in the Upgrade header field.[60]
 * @type {Number}
 */
const UPGRADE_REQUIRED = 426;

/**
 * Precondition Required (RFC 6585)
 *
 * The origin server requires the request to be conditional. Intended to prevent "the 'lost update' problem, where a client GETs a resource's state, modifies it, and PUTs it back to the server, when meanwhile a third party has modified the state on the server, leading to a conflict."[61]
 * @type {Number}
 */
const PRECONDITION_REQUIRED = 428;

/**
 * Too Many Requests (RFC 6585)
 *
 * The user has sent too many requests in a given amount of time. Intended for use with rate limiting schemes.[61]
 * @type {Number}
 */
const TOO_MANY_REQUESTS = 429;

/**
 * Request Header Fields Too Large (RFC 6585)
 *
 * The server is unwilling to process the request because either an individual header field, or all the header fields collectively, are too large.[61]
 * @type {Number}
 */
const REQUEST_HEADER_FIELDS_TOO_LARGE = 431;

/**
 * Login Timeout (Microsoft)
 *
 * A Microsoft extension. Indicates that your session has expired.[62]
 * @type {Number}
 */
const LOGIN_TIMEOUT = 440;

/**
 * No Response (Nginx)
 *
 * Used in Nginx logs to indicate that the server has returned no information to the client and closed the connection (useful as a deterrent for malware).[63]
 * @type {Number}
 */
const NO_RESPONSE = 444;

/**
 * Retry With (Microsoft)
 *
 * A Microsoft extension. The request should be retried after performing the appropriate action.[64]
 * @type {Number}
 */
const RETRY_WITH = 449;

/**
 * Blocked by Windows Parental Controls (Microsoft)
 *
 * A Microsoft extension. This error is given when Windows Parental Controls are turned on and are blocking access to the given webpage.[65]
 * @type {Number}
 */
const BLOCKED_BY_WINDOWS_PARENTAL_CONTROLS = 450;

/**
 * Unavailable For Legal Reasons (Internet draft)
 *
 * Defined in the internet draft "A New HTTP Status Code for Legally-restricted Resources".[66] Intended to be used when resource access is denied for legal reasons, e.g. censorship or government-mandated blocked access. A reference to the 1953 dystopian novel Fahrenheit 451, where books are outlawed.[67]
 * @type {Number}
 */
const UNAVAILABLE_FOR_LEGAL_REASONS = 451;

/**
 * Redirect (Microsoft)
 *
 * Used in Exchange ActiveSync if there either is a more efficient server to use or the server cannot access the users' mailbox.[68] The client is supposed to re-run the HTTP Autodiscovery protocol to find a better suited server.[69]
 * @type {Number}
 */
const REDIRECT = 451;

/**
 * Request Header Too Large (Nginx)
 *
 * Nginx internal code similar to 431 but it was introduced earlier in version 0.9.4 (on January 21, 2011).[70][original research?]
 * @type {Number}
 */
const REQUEST_HEADER_TOO_LARGE = 494;

/**
 * Cert Error (Nginx)
 *
 * Nginx internal code used when SSL client certificate error occurred to distinguish it from 4XX in a log and an error page redirection.[71]
 * @type {Number}
 */
const CERT_ERROR = 495;

/**
 * No Cert (Nginx)
 *
 * Nginx internal code used when client didn't provide certificate to distinguish it from 4XX in a log and an error page redirection.[72]
 * @type {Number}
 */
const NO_CERT = 496;

/**
 * HTTP to HTTPS (Nginx)
 *
 * Nginx internal code used for the plain HTTP requests that are sent to HTTPS port to distinguish it from 4XX in a log and an error page redirection.[73]
 * @type {Number}
 */
const HTTP_TO_HTTPS = 497;

/**
 * Token expired/invalid (Esri)
 *
 * Returned by ArcGIS for Server. A code of 498 indicates an expired or otherwise invalid token.[74]
 * @type {Number}
 */
const TOKEN_EXPIRED_INVALID = 498;

/**
 * Client Closed Request (Nginx)
 *
 * Used in Nginx logs to indicate when the connection has been closed by client while the server is still processing its request, making server unable to send a status code back.[75]
 * @type {Number}
 */
const CLIENT_CLOSED_REQUEST = 499;

/**
 * Token required (Esri)
 *
 * Returned by ArcGIS for Server. A code of 499 indicates that a token is required (if no token was submitted).[74]
 * @type {Number}
 */
const TOKEN_REQUIRED = 499;

/**
 * Internal Server Error
 *
 * A generic error message, given when an unexpected condition was encountered and no more specific message is suitable.[78]
 * @type {Number}
 */
const INTERNAL_SERVER_ERROR = 500;

/**
 * Not Implemented
 *
 * The server either does not recognize the request method, or it lacks the ability to fulfill the request. Usually this implies future availability (e.g., a new feature of a web-service API).[79]
 * @type {Number}
 */
const NOT_IMPLEMENTED = 501;

/**
 * Bad Gateway
 *
 * The server was acting as a gateway or proxy and received an invalid response from the upstream server.[80]
 * @type {Number}
 */
const BAD_GATEWAY = 502;

/**
 * Service Unavailable
 *
 * The server is currently unavailable (because it is overloaded or down for maintenance). Generally, this is a temporary state.[81]
 * @type {Number}
 */
const SERVICE_UNAVAILABLE = 503;

/**
 * Gateway Timeout
 *
 * The server was acting as a gateway or proxy and did not receive a timely response from the upstream server.[82]
 * @type {Number}
 */
const GATEWAY_TIMEOUT = 504;

/**
 * HTTP Version Not Supported
 *
 * The server does not support the HTTP protocol version used in the request.[83]
 * @type {Number}
 */
const HTTP_VERSION_NOT_SUPPORTED = 505;

/**
 * Variant Also Negotiates (RFC 2295)
 *
 * Transparent content negotiation for the request results in a circular reference.[84]
 * @type {Number}
 */
const VARIANT_ALSO_NEGOTIATES = 506;

/**
 * Insufficient Storage (WebDAV, RFC 4918)
 *
 * The server is unable to store the representation needed to complete the request.[17]
 * @type {Number}
 */
const INSUFFICIENT_STORAGE = 507;

/**
 * Loop Detected (WebDAV, RFC 5842)
 *
 * The server detected an infinite loop while processing the request (sent in lieu of 208 Already Reported).
 * @type {Number}
 */
const LOOP_DETECTED = 508;

/**
 * Bandwidth Limit Exceeded (Apache bw/limited extension)[85]
 *
 * This status code is not specified in any RFCs. Its use is unknown.
 * @type {Number}
 */
const BANDWIDTH_LIMIT_EXCEEDED = 509;

/**
 * Not Extended (RFC 2774)
 *
 * Further extensions to the request are required for the server to fulfil it.[86]
 * @type {Number}
 */
const NOT_EXTENDED = 510;

/**
 * Network Authentication Required (RFC 6585)
 *
 * The client needs to authenticate to gain network access. Intended for use by intercepting proxies used to control access to the network (e.g., "captive portals" used to require agreement to Terms of Service before granting full Internet access via a Wi-Fi hotspot).[61]
 * @type {Number}
 */
const NETWORK_AUTHENTICATION_REQUIRED = 511;

/**
 * Unknown Error
 *
 * This status code is not specified in any RFC and is returned by certain services, for instance Microsoft Azure and CloudFlare servers = "The 520 error is essentially a "catch-all" response for when the origin server returns something unexpected or something that is not tolerated/interpreted (protocol violation or empty response)."[87]
 * @type {Number}
 */
const UNKNOWN_ERROR = 520;

/**
 * Origin Connection Time-out
 *
 * This status code is not specified in any RFCs, but is used by CloudFlare's reverse proxies to signal that a server connection timed out.
 * @type {Number}
 */
const ORIGIN_CONNECTION_TIMEOUT = 522;

/**
 * Network read timeout error (Unknown)
 *
 * This status code is not specified in any RFCs, but is used by Microsoft HTTP proxies to signal a network read timeout behind the proxy to a client in front of the proxy.[citation needed][88]
 * @type {Number}
 */
const NETWORK_READ_TIMEOUT = 598;

/**
 * Network connect timeout error (Unknown)
 *
 * This status code is not specified in any RFCs, but is used by Microsoft HTTP proxies to signal a network connect timeout behind the proxy to a client in front of the proxy.[citation needed][89]
 * @type {Number}
 */
const NETWORK_CONNECT_TIMEOUT_ERROR = 599;

var _codes = /*#__PURE__*/Object.freeze({
	CONTINUE: CONTINUE,
	SWITCHING_PROTOCOLS: SWITCHING_PROTOCOLS,
	PROCESSING: PROCESSING,
	OK: OK,
	CREATED: CREATED,
	ACCEPTED: ACCEPTED,
	NON_AUTHORITATIVE_INFORMATION: NON_AUTHORITATIVE_INFORMATION,
	NO_CONTENT: NO_CONTENT,
	RESET_CONTENT: RESET_CONTENT,
	PARTIAL_CONTENT: PARTIAL_CONTENT,
	MULTI_STATUS: MULTI_STATUS,
	ALREADY_REPORTED: ALREADY_REPORTED,
	IM_USED: IM_USED,
	MULTIPLE_CHOICES: MULTIPLE_CHOICES,
	MOVED_PERMANENTLY: MOVED_PERMANENTLY,
	FOUND: FOUND,
	SEE_OTHER: SEE_OTHER,
	NOT_MODIFIED: NOT_MODIFIED,
	USE_PROXY: USE_PROXY,
	SWITCH_PROXY: SWITCH_PROXY,
	TEMPORARY_REDIRECT: TEMPORARY_REDIRECT,
	PERMANENT_REDIRECT: PERMANENT_REDIRECT,
	RESUME_INCOMPLETE: RESUME_INCOMPLETE,
	BAD_REQUEST: BAD_REQUEST,
	UNAUTHORIZED: UNAUTHORIZED,
	PAYMENT_REQUIRED: PAYMENT_REQUIRED,
	FORBIDDEN: FORBIDDEN,
	NOT_FOUND: NOT_FOUND,
	METHOD_NOT_ALLOWED: METHOD_NOT_ALLOWED,
	NOT_ACCEPTABLE: NOT_ACCEPTABLE,
	PROXY_AUTHENTICATION_REQUIRED: PROXY_AUTHENTICATION_REQUIRED,
	REQUEST_TIMEOUT: REQUEST_TIMEOUT,
	CONFLICT: CONFLICT,
	GONE: GONE,
	LENGTH_REQUIRED: LENGTH_REQUIRED,
	PRECONDITION_FAILED: PRECONDITION_FAILED,
	PAYLOAD_TOO_LARGE: PAYLOAD_TOO_LARGE,
	URI_TOO_LONG: URI_TOO_LONG,
	UNSUPPORTED_MEDIA_TYPE: UNSUPPORTED_MEDIA_TYPE,
	RANGE_NOT_SATISFIABLE: RANGE_NOT_SATISFIABLE,
	EXPECTATION_FAILED: EXPECTATION_FAILED,
	IM_A_TEAPOT: IM_A_TEAPOT,
	AUTHENTICATION_TIMEOUT: AUTHENTICATION_TIMEOUT,
	METHOD_FAILURE: METHOD_FAILURE,
	ENHANCE_YOUR_CALM: ENHANCE_YOUR_CALM,
	MISDIRECTED_REQUEST: MISDIRECTED_REQUEST,
	UNPROCESSABLE_ENTITY: UNPROCESSABLE_ENTITY,
	LOCKED: LOCKED,
	FAILED_DEPENDENCY: FAILED_DEPENDENCY,
	UPGRADE_REQUIRED: UPGRADE_REQUIRED,
	PRECONDITION_REQUIRED: PRECONDITION_REQUIRED,
	TOO_MANY_REQUESTS: TOO_MANY_REQUESTS,
	REQUEST_HEADER_FIELDS_TOO_LARGE: REQUEST_HEADER_FIELDS_TOO_LARGE,
	LOGIN_TIMEOUT: LOGIN_TIMEOUT,
	NO_RESPONSE: NO_RESPONSE,
	RETRY_WITH: RETRY_WITH,
	BLOCKED_BY_WINDOWS_PARENTAL_CONTROLS: BLOCKED_BY_WINDOWS_PARENTAL_CONTROLS,
	UNAVAILABLE_FOR_LEGAL_REASONS: UNAVAILABLE_FOR_LEGAL_REASONS,
	REDIRECT: REDIRECT,
	REQUEST_HEADER_TOO_LARGE: REQUEST_HEADER_TOO_LARGE,
	CERT_ERROR: CERT_ERROR,
	NO_CERT: NO_CERT,
	HTTP_TO_HTTPS: HTTP_TO_HTTPS,
	TOKEN_EXPIRED_INVALID: TOKEN_EXPIRED_INVALID,
	CLIENT_CLOSED_REQUEST: CLIENT_CLOSED_REQUEST,
	TOKEN_REQUIRED: TOKEN_REQUIRED,
	INTERNAL_SERVER_ERROR: INTERNAL_SERVER_ERROR,
	NOT_IMPLEMENTED: NOT_IMPLEMENTED,
	BAD_GATEWAY: BAD_GATEWAY,
	SERVICE_UNAVAILABLE: SERVICE_UNAVAILABLE,
	GATEWAY_TIMEOUT: GATEWAY_TIMEOUT,
	HTTP_VERSION_NOT_SUPPORTED: HTTP_VERSION_NOT_SUPPORTED,
	VARIANT_ALSO_NEGOTIATES: VARIANT_ALSO_NEGOTIATES,
	INSUFFICIENT_STORAGE: INSUFFICIENT_STORAGE,
	LOOP_DETECTED: LOOP_DETECTED,
	BANDWIDTH_LIMIT_EXCEEDED: BANDWIDTH_LIMIT_EXCEEDED,
	NOT_EXTENDED: NOT_EXTENDED,
	NETWORK_AUTHENTICATION_REQUIRED: NETWORK_AUTHENTICATION_REQUIRED,
	UNKNOWN_ERROR: UNKNOWN_ERROR,
	ORIGIN_CONNECTION_TIMEOUT: ORIGIN_CONNECTION_TIMEOUT,
	NETWORK_READ_TIMEOUT: NETWORK_READ_TIMEOUT,
	NETWORK_CONNECT_TIMEOUT_ERROR: NETWORK_CONNECT_TIMEOUT_ERROR
});

/* globals */

/**
 * @typedef {Object} CONSTSHEADERSREQUEST
 */

/**
 * Content-Types that are acceptable for the response. See Content negotiation. Permanent.
 * Examples:
 * <code>Accept: text/plain</code>
 * @type {String}
 */
const ACCEPT = 'Accept';

/**
 * Character sets that are acceptable. Permanent.
 * Examples:
 * <code>Accept-Charset: utf-8</code>
 * @type {String}
 */
const ACCEPT_CHARSET = 'Accept-Charset';

/**
 * List of acceptable encodings. See HTTP compression. Permanent.
 * Examples:
 * <code>Accept-Encoding: gzip, deflate</code>
 * @type {String}
 */
const ACCEPT_ENCODING = 'Accept-Encoding';

/**
 * List of acceptable human languages for response. See Content negotiation. Permanent.
 * Examples:
 * <code>Accept-Language: en-US</code>
 * @type {String}
 */
const ACCEPT_LANGUAGE = 'Accept-Language';

/**
 * Acceptable version in time. Provisional.
 * Examples:
 * <code>Accept-Datetime: Thu, 31 May 2007 20:35:00 GMT</code>
 * @type {String}
 */
const ACCEPT_DATETIME = 'Accept-Datetime';

/**
 * Authentication credentials for HTTP authentication. Permanent.
 * Examples:
 * <code>Authorization: Basic QWxhZGRpbjpvcGVuIHNlc2FtZQ==</code>
 * @type {String}
 */
const AUTHORIZATION = 'Authorization';

/**
 * Used to specify directives that must be obeyed by all caching mechanisms along the request-response chain.
 * Permanent.
 * Examples:
 * <code>Cache-Control: no-cache</code>
 * @type {String}
 */
const CACHE_CONTROL = 'Cache-Control';

/**
 * Control options for the current connection and list of hop-by-hop request fields. Permanent.
 * Examples:
 * <code>Connection: keep-alive</code>
 * <code>Connection: Upgrade</code>
 * @type {String}
 */
const CONNECTION = 'Connection';

/**
 * An HTTP cookie previously sent by the server with Set-Cookie (below). Permanent: standard.
 * Examples:
 * <code>Cookie: $Version=1, Skin=new,</code>
 * @type {String}
 */
const COOKIE = 'Cookie';

/**
 * The length of the request body in octets (8-bit bytes). Permanent.
 * Examples:
 * <code>Content-Length: 348</code>
 * @type {String}
 */
const CONTENT_LENGTH = 'Content-Length';

/**
 * A Base64-encoded binary MD5 sum of the content of the request body. Obsolete.
 * Examples:
 * <code>Content-MD5: Q2hlY2sgSW50ZWdyaXR5IQ==</code>
 * @type {String}
 */
const CONTENT_MD5 = 'Content-MD5';

/**
 * The MIME type of the body of the request (used with POST and PUT requests). Permanent.
 * <code>Content-Type: application/x-www-form-urlencoded</code>
 * @type {String}
 */
const CONTENT_TYPE = 'Content-Type';

/**
 * The date and time that the message was sent (in "HTTP-date" format as defined by RFC 7231 Date/Time Formats).
 * Permanent.
 * Examples:
 * <code>Date: Tue, 15 Nov 1994 08:12:31 GMT</code>
 * @type {String}
 */
const DATE = 'Date';

/**
 * Indicates that particular server behaviors are required by the client. Permanent.
 * Examples:
 * <code>Expect: 100-continue</code>
 * @type {String}
 */
const EXPECT = 'Expect';

/**
 * The email address of the user making the request. Permanent.
 * Examples:
 * <code>From: user@example.com</code>
 * @type {String}
 */
const FROM = 'From';

/**
 * The domain name of the server (for virtual hosting), and the TCP port number on which the server is listening. The
 * port number may be omitted if the port is the standard port for the service requested. Permanent. Mandatory since
 * HTTP/1.1.
 * Examples:
 * <code>Host: en.wikipedia.org:80</code>
 * <code>Host: en.wikipedia.org</code>
 * @type {String}
 */
const HOST = 'Host';

/**
 * Only perform the action if the client supplied entity matches the same entity on the server. This is mainly for
 * methods like PUT to only update a resource if it has not been modified since the user last updated it. Permanent.
 * Examples:
 * <code>If-Match: "737060cd8c284d8af7ad3082f209582d"</code>
 * @type {String}
 */
const IF_MATCH = 'If-Match';

/**
 * Allows a 304 Not Modified to be returned if content is unchanged. Permanent.
 * Examples:
 * <code>If-Modified-Since: Sat, 29 Oct 1994 19:43:31 GMT</code>
 * @type {String}
 */
const IF_MODIFIED_SINCE = 'If-Modified-Since';

/**
 * Allows a 304 Not Modified to be returned if content is unchanged, see HTTP ETag. Permanent.
 * Examples:
 * <code>If-None-Match: "737060cd8c284d8af7ad3082f209582d"</code>
 * @type {String}
 */
const IF_NONE_MATCH = 'If-None-Match';

/**
 * If the entity is unchanged, send me the part(s) that I am missing, otherwise, send me the entire new entity.
 * Permanent.
 * Examples:
 * <code>If-Range: "737060cd8c284d8af7ad3082f209582d"</code>
 * @type {String}
 */
const IF_RANGE = 'If-Range';

/**
 * Only send the response if the entity has not been modified since a specific time. Permanent.
 * Examples:
 * <code>If-Unmodified-Since: Sat, 29 Oct 1994 19:43:31 GMT</code>
 * @type {String}
 */
const IF_UNMODIFIED_SINCE = 'If-Unmodified-Since';

/**
 * Limit the number of times the message can be forwarded through proxies or gateways. Permanent.
 * Examples:
 * <code>Max-Forwards: 10</code>
 * @type {String}
 */
const MAX_FORWARDS = 'Max-Forwards';

/**
 * Initiates a request for cross-origin resource sharing (asks server for an 'Access-Control-Allow-Origin' response
 * field). Permanent: standard.
 * Examples:
 * <code>Origin: http://www.example-social-network.com</code>
 * @type {String}
 */
const ORIGIN = 'Origin';

/**
 * Implementation-specific fields that may have various effects anywhere along the request-response chain. Permanent.
 * Examples:
 * <code>Pragma: no-cache</code>
 * @type {String}
 */
const PRAGMA = 'Pragma';

/**
 * Authorization credentials for connecting to a proxy. Permanent.
 * Examples:
 * <code>Proxy-Authorization: Basic QWxhZGRpbjpvcGVuIHNlc2FtZQ==</code>
 * @type {String}
 */
const PROXY_AUTHORIZATION = 'Proxy-Authorization';

/**
 * Request only part of an entity. Bytes are numbered from 0. See Byte serving. Permanent.
 * Examples:
 * <code>Range: bytes=500-999</code>
 * @type {String}
 */
const RANGE = 'Range';

/**
 * This is the address of the previous web page from which a link to the currently requested page was followed. (The
 * word "referrer" has been misspelled in the RFC as well as in most implementations to the point that it has become
 * standard usage and is considered correct terminology). Permanent.
 * Examples:
 * <code>Referer: http://en.wikipedia.org/wiki/Main_Page</code>
 * @type {String}
 */
const REFERER = 'Referer';

/**
 * The transfer encodings the user agent is willing to accept: the same values as for the response header field
 * Transfer-Encoding can be used, plus the "trailers" value (related to the "chunked" transfer method) to notify the
 * server it expects to receive additional fields in the trailer after the last, zero-sized, chunk. Permanent.
 * Examples:
 * <code>TE: trailers, deflate</code>
 * @type {String}
 */
const TE = 'TE';

/**
 * The user agent string of the user agent. Permanent.
 * Examples:
 * <code>User-Agent: Mozilla/5.0 (X11, Linux x86_64, rv:12.0) Gecko/20100101 Firefox/21.0</code>
 * @type {String}
 */
const USER_AGENT = 'User-Agent';

/**
 * Ask the server to upgrade to another protocol. Permanent.
 * Examples:
 * <code>Upgrade: HTTP/2.0, SHTTP/1.3, IRC/6.9, RTA/x11</code>
 * @type {String}
 */
const UPGRADE = 'Upgrade';

/**
 * Informs the server of proxies through which the request was sent. Permanent.
 * Examples:
 * <code>Via: 1.0 fred, 1.1 example.com (Apache/1.1)</code>
 * @type {String}
 */
const VIA = 'Via';

/**
 * A general warning about possible problems with the entity body. Permanent.
 * Examples:
 * <code>Warning: 199 Miscellaneous warning</code>
 * @type {String}
 */
const WARNING = 'Warning';

/**
 * mainly used to identify Ajax requests. Most JavaScript frameworks send this field with value of XMLHttpRequest.
 * Examples:
 * <code>X-Requested-With: XMLHttpRequest</code>
 * @type {String}
 */
const X_REQUESTED_WITH = 'X-Requested-With';

/**
 * Requests a web application to disable their tracking of a user. This is Mozilla's version of the X-Do-Not-Track
 * header field (since Firefox 4.0 Beta 11). Safari and IE9 also have support for this field. On March 7, 2011, a
 * draft proposal was submitted to IETF. The W3C Tracking Protection Working Group is producing a specification.
 * Examples:
 * <code>DNT: 1 (Do Not Track Enabled)</code>
 * <code>DNT: 0 (Do Not Track Disabled)</code>
 * @type {String}
 */
const DNT = 'DNT';

/**
 * A de facto standard for identifying the originating IP address of a client connecting to a web server through an
 * HTTP proxy or load balancer.
 * Examples:
 * <code>X-Forwarded-For: client1, proxy1, proxy2</code>
 * <code>X-Forwarded-For: 129.78.138.66, 129.78.64.103</code>
 * @type {String}
 */
const X_FORWARDED_FOR = 'X-Forwarded-For';


/**
 * A de facto standard for identifying the original host requested by the client in the Host HTTP request header, since
 * the host name and/or port of the reverse proxy (load balancer) may differ from the origin server handling the
 * request.
 * Examples:
 * <code>X-Forwarded-Host: en.wikipedia.org:80</code>
 * <code>X-Forwarded-Host: en.wikipedia.org</code>
 * @type {String}
 */
const X_FORWARDED_HOST = 'X-Forwarded-Host';

/**
 * A de facto standard for identifying the originating protocol of an HTTP request, since a reverse proxy (load
 * balancer) may communicate with a web server using HTTP even if the request to the reverse proxy is HTTPS. An
 * alternative form of the header (X-ProxyUser-Ip) is used by Google clients talking to Google servers.
 * Examples:
 * <code>X-Forwarded-Proto: https</code>
 * @type {String}
 */
const X_FORWARDED_PROTO = 'X-Forwarded-Proto';

/**
 * Non-standard header field used by Microsoft applications and load-balancers.
 * Examples:
 * <code>Front-End-Https: on</code>
 * @type {String}
 */
const FRONT_END_HTTPS = 'Front-End-Https';

/**
 * Requests a web application override the method specified in the request (typically POST) with the method given in
 * the header field (typically PUT or DELETE). Can be used when a user agent or firewall prevents PUT or DELETE methods
 * from being sent directly (note that this either a bug in the software component, which ought to be fixed, or an
 * intentional configuration, in which case bypassing it may be the wrong thing to do).
 * Examples:
 * <code>X-HTTP-Method-Override: DELETE</code>
 * @type {String}
 */
const X_HTTP_METHOD_OVERRIDE = 'X-Http-Method-Override';

/**
 * Allows easier parsing of the MakeModel/Firmware that is usually found in the User-Agent String of AT&T Devices.
 * Examples:
 * <code>X-Att-Deviceid: GT-P7320/P7320XXLPG</code>
 * @type {String}
 */
const X_ATT_DEVICEID = 'X-ATT-DeviceId';

/**
 * Links to an XML file on the Internet with a full description and details about the device currently connecting. In the example to the right is an XML file for an AT&T Samsung Galaxy S2.    x-wap-profile: http://wap.samsungmobile.com/uaprof/SGH-I777.xml
 */
const X_WAP_PROFILE = 'X-Wap-Profile';

var _request = /*#__PURE__*/Object.freeze({
	ACCEPT: ACCEPT,
	ACCEPT_CHARSET: ACCEPT_CHARSET,
	ACCEPT_ENCODING: ACCEPT_ENCODING,
	ACCEPT_LANGUAGE: ACCEPT_LANGUAGE,
	ACCEPT_DATETIME: ACCEPT_DATETIME,
	AUTHORIZATION: AUTHORIZATION,
	CACHE_CONTROL: CACHE_CONTROL,
	CONNECTION: CONNECTION,
	COOKIE: COOKIE,
	CONTENT_LENGTH: CONTENT_LENGTH,
	CONTENT_MD5: CONTENT_MD5,
	CONTENT_TYPE: CONTENT_TYPE,
	DATE: DATE,
	EXPECT: EXPECT,
	FROM: FROM,
	HOST: HOST,
	IF_MATCH: IF_MATCH,
	IF_MODIFIED_SINCE: IF_MODIFIED_SINCE,
	IF_NONE_MATCH: IF_NONE_MATCH,
	IF_RANGE: IF_RANGE,
	IF_UNMODIFIED_SINCE: IF_UNMODIFIED_SINCE,
	MAX_FORWARDS: MAX_FORWARDS,
	ORIGIN: ORIGIN,
	PRAGMA: PRAGMA,
	PROXY_AUTHORIZATION: PROXY_AUTHORIZATION,
	RANGE: RANGE,
	REFERER: REFERER,
	TE: TE,
	USER_AGENT: USER_AGENT,
	UPGRADE: UPGRADE,
	VIA: VIA,
	WARNING: WARNING,
	X_REQUESTED_WITH: X_REQUESTED_WITH,
	DNT: DNT,
	X_FORWARDED_FOR: X_FORWARDED_FOR,
	X_FORWARDED_HOST: X_FORWARDED_HOST,
	X_FORWARDED_PROTO: X_FORWARDED_PROTO,
	FRONT_END_HTTPS: FRONT_END_HTTPS,
	X_HTTP_METHOD_OVERRIDE: X_HTTP_METHOD_OVERRIDE,
	X_ATT_DEVICEID: X_ATT_DEVICEID,
	X_WAP_PROFILE: X_WAP_PROFILE
});

/* globals */

/**
 * @typedef {Object} CONSTSHEADERSRESPONSE
 */

/**
 * Implemented as a misunderstanding of the HTTP specifications. Common because of mistakes in implementations of early HTTP versions. Has exactly the same functionality as standard Connection field.    Proxy-Connection: keep-alive
 * @type {String}
 */
const PROXY_CONNECTION = 'Proxy-Connection';

/**
 * Server-side deep packet insertion of a unique ID identifying customers of Verizon Wireless, also known as "perma-cookie" or "supercookie"    X-UIDH: ...
 */
const X_UIDH = 'X-UIDH';

/**
 * Used to prevent cross-site request forgery. Alternative header names are: X-CSRFToken and X-XSRF-TOKEN    X-Csrf-Token: i8XNjC4b8KVok4uw5RftR38Wgp2BFwql
 */
const X_CSRF_TOKEN = 'X-Csrf-Token';

/**
 * Specifying which web sites can participate in cross-origin resource sharing    Access-Control-Allow-Origin: *    Provisional
 */
const ACCESS_CONTROL_ALLOW_ORIGIN = 'Access-Control-Allow-Origin';

/**
 * Specifies which patch document formats this server supports    Accept-Patch: text/example,charset=utf-8    Permanent
 */
const ACCEPT_PATCH = 'Accept-Patch';

/**
 * What partial content range types this server supports via byte serving    Accept-Ranges: bytes    Permanent
 */
const ACCEPT_RANGES = 'Accept-Ranges';

/**
 * The age the object has been in a proxy cache in seconds    Age: 12    Permanent
 */
const AGE = 'Age';

/**
 * Valid actions for a specified resource. To be used for a 405 Method not allowed    Allow: GET, HEAD    Permanent
 */
const ALLOW = 'Allow';

/**
 * Tells all caching mechanisms from server to client whether they may cache this object. It is measured in seconds    Cache-Control: max-age=3600    Permanent
 */
const CACHE_CONTROL$1 = 'Cache-Control';

/**
 * Control options for the current connection and list of hop-by-hop response fields    Connection: close    Permanent
 */
const CONNECTION$1 = 'Connection';

/**
 * An opportunity to raise a "File Download" dialogue box for a known MIME type with binary format or suggest a filename for dynamic content. Quotes are necessary with special characters.    Content-Disposition: attachment, filename="fname.ext"    Permanent
 */
const CONTENT_DISPOSITION = 'Content-Disposition';

/**
 * The type of encoding used on the data. See HTTP compression.    Content-Encoding: gzip    Permanent
 */
const CONTENT_ENCODING = 'Content-Encoding';

/**
 * The natural language or languages of the intended audience for the enclosed content    Content-Language: da    Permanent
 */
const CONTENT_LANGUAGE = 'Content-Language';

/**
 * The length of the response body in octets (8-bit bytes)    Content-Length: 348    Permanent
 */
const CONTENT_LENGTH$1 = 'Content-Length';

/**
 * An alternate location for the returned data    Content-Location: /index.htm    Permanent
 */
const CONTENT_LOCATION = 'Content-Location';

/**
 * A Base64-encoded binary MD5 sum of the content of the response    Content-MD5: Q2hlY2sgSW50ZWdyaXR5IQ==    Obsolete
 */
const CONTENT_MD5$1 = 'Content-MD5';

/**
 * Where in a full body message this partial message belongs    Content-Range: bytes 21010-47021/47022    Permanent
 */
const CONTENT_RANGE = 'Content-Range';

/**
 * The MIME type of this content    Content-Type: text/html, charset=utf-8    Permanent
 */
const CONTENT_TYPE$1 = 'Content-Type';

/**
 * The date and time that the message was sent (in "HTTP-date" format as defined by RFC 7231)    Date: Tue, 15 Nov 1994 08:12:31 GMT    Permanent
 */
const DATE$1 = 'Date';

/**
 * An identifier for a specific version of a resource, often a message digest    ETag: "737060cd8c284d8af7ad3082f209582d"    Permanent
 */
const ETAG = 'ETag';

/**
 * Gives the date/time after which the response is considered stale (in "HTTP-date" format as defined by RFC 7231)    Expires: Thu, 01 Dec 1994 16:00:00 GMT    Permanent: standard
 */
const EXPIRES = 'Expires';

/**
 * The last modified date for the requested object (in "HTTP-date" format as defined by RFC 7231)    Last-Modified: Tue, 15 Nov 1994 12:45:26 GMT    Permanent
 */
const LAST_MODIFIED = 'Last-Modified';

/**
 * Used to express a typed relationship with another resource, where the relation type is defined by RFC 5988    Link: </feed>, rel="alternate"    Permanent
 */
const LINK = 'Link';

/**
 * Used in redirection, or when a new resource has been created.    Location: http://www.w3.org/pub/WWW/People.html    Permanent
 */
const LOCATION = 'Location';

/**
 * This field is supposed to set P3P policy, in the form of P3P:CP="your_compact_policy". However, P3P did not take off, most browsers have never fully implemented it, a lot of websites set this field with fake policy text, that was enough to fool browsers the existence of P3P policy and grant permissions for third party cookies.    P3P: CP="This is not a P3P policy! See http://www.google.com/support/accounts/bin/answer.py?hl=en&answer=151657 for more info."    Permanent
 * @type {string}
 */
const P3P = 'P3P';

/**
 * Implementation-specific fields that may have various effects anywhere along the request-response chain.    Pragma: no-cache    Permanent
 */
const PRAGMA$1 = 'Pragma';

/**
 * Request authentication to access the proxy.    Proxy-Authenticate: Basic    Permanent
 */
const PROXY_AUTHENTICATION = 'Proxy-Authenticate';

/**
 * HTTP Public Key Pinning, announces hash of website's authentic TLS certificate    Public-Key-Pins: max-age=2592000, pin-sha256="E9CZ9INDbd+2eRQozYqqbQ2yXLVKB9+xcprMF+44U1g=",    Permanent
 */
const PUBLIC_KEY_PINS = 'Public-Key-Pins';

/**
 * Used in redirection, or when a new resource has been created. This refresh redirects after 5 seconds.    Refresh: 5, url=http://www.w3.org/pub/WWW/People.html    Proprietary and non-standard: a header extension introduced by Netscape and supported by most web browsers.
 */
const REFRESH = 'Refresh';

/**
 * If an entity is temporarily unavailable, this instructs the client to try again later. Value could be a specified period of time (in seconds) or a HTTP-date.

 Example 1: Retry-After: 120
 Example 2: Retry-After: Fri, 07 Nov 2014 23:59:59 GMT
 Permanent
 *
 */
const RETRY_AFTER = 'Retry-After';

/**
 * A name for the server    Server: Apache/2.4.1 (Unix)    Permanent
 */
const SERVER = 'Server';

/**
 * An HTTP cookie    Set-Cookie: UserID=JohnDoe, Max-Age=3600, Version=1    Permanent: standard
 */
const SET_COOKIE = 'Set-Cookie';

/**
 * CGI header field specifying the status of the HTTP response. Normal HTTP responses use a separate "Status-Line" instead, defined by RFC 7230.    Status: 200 OK    Not listed as a registered field name
 */
const STATUS = 'Status';

/**
 * A HSTS Policy informing the HTTP client how long to cache the HTTPS only policy and whether this applies to subdomains.    Strict-Transport-Security: max-age=16070400, includeSubDomains    Permanent: standard
 */
const STRICT_TRANSPORT_SECURITY = 'Strict-Transport-Security';

/**
 * The Trailer general field value indicates that the given set of header fields is present in the trailer of a message encoded with chunked transfer coding.    Trailer: Max-Forwards    Permanent
 */
const TRAILER = 'Trailer';

/**
 * The form of encoding used to safely transfer the entity to the user. Currently defined methods are: chunked, compress, deflate, gzip, identity.    Transfer-Encoding: chunked    Permanent
 */
const TRANSFER_ENCODING = 'Transfer-Encoding';

/**
 * Ask the client to upgrade to another protocol.    Upgrade: HTTP/2.0, SHTTP/1.3, IRC/6.9, RTA/x11    Permanent
 */
const UPGRADE$1 = 'Upgrade';

/**
 * Tells downstream proxies how to match future request headers to decide whether the cached response can be used rather than requesting a fresh one from the origin server.    Vary: *    Permanent
 */
const VARY = 'Vary';

/**
 * Informs the client of proxies through which the response was sent.    Via: 1.0 fred, 1.1 example.com (Apache/1.1)    Permanent
 */
const VIA$1 = 'Via';

/**
 * A general warning about possible problems with the entity body.    Warning: 199 Miscellaneous warning    Permanent
 */
const WARNING$1 = 'Warning';

/**
 * Indicates the authentication scheme that should be used to access the requested entity.    WWW-Authenticate: Basic    Permanent
 */
const WWW_AUTHENTICATE = 'WWW-Authenticate';

/**
 * Clickjacking protection: deny - no rendering within a frame, sameorigin - no rendering if origin mismatch, allow-from - allow from specified location, allowall - non-standard, allow from any location    X-Frame-Options: deny    Obsolete
 */
const X_FRAME_OPTIONS = 'X-Frame-Options';

/**
 * Cross-site scripting (XSS) filter    X-XSS-Protection: 1, mode=block
 */
const X_XSS_PROTECTION = 'X-XSS-Protection';

/**
 * X-Content-Security-Policy, X-WebKit-CSP    Content Security Policy definition.    X-WebKit-CSP: default-src 'self'
 */
const CONTENT_SECURITY_POLICY = 'Content-Security-Policy,';

/**
 * The only defined value, "nosniff", prevents Internet Explorer from MIME-sniffing a response away from the declared content-type. This also applies to Google Chrome, when downloading extensions.    X-Content-Type-Options: nosniff
 */
const X_CONTENT_TYPE_OPTIONS = 'X-Content-Type-Options';

/**
 * specifies the technology (e.g. ASP.NET, PHP, JBoss) supporting the web application (version details are often in X-Runtime, X-Version, or X-AspNet-Version)    X-Powered-By: PHP/5.4.0
 */
const X_POWERED_BY = 'X-Powered-By';

/**
 * Recommends the preferred rendering engine (often a backward-compatibility mode) to use to display the content. Also used to activate Chrome Frame in Internet Explorer.    X-UA-Compatible: IE=EmulateIE7

 X-UA-Compatible: IE=edge
 X-UA-Compatible: Chrome=1
 */
const X_UA_COMPATIBLE = 'X-UA-Compatible';

/**
 * Provide the duration of the audio or video in seconds, only supported by Gecko browsers    X-Content-Duration: 42.666
 */
const X_CONTENT_DURATION = 'X-Content-Duration';

var _response = /*#__PURE__*/Object.freeze({
	PROXY_CONNECTION: PROXY_CONNECTION,
	X_UIDH: X_UIDH,
	X_CSRF_TOKEN: X_CSRF_TOKEN,
	ACCESS_CONTROL_ALLOW_ORIGIN: ACCESS_CONTROL_ALLOW_ORIGIN,
	ACCEPT_PATCH: ACCEPT_PATCH,
	ACCEPT_RANGES: ACCEPT_RANGES,
	AGE: AGE,
	ALLOW: ALLOW,
	CACHE_CONTROL: CACHE_CONTROL$1,
	CONNECTION: CONNECTION$1,
	CONTENT_DISPOSITION: CONTENT_DISPOSITION,
	CONTENT_ENCODING: CONTENT_ENCODING,
	CONTENT_LANGUAGE: CONTENT_LANGUAGE,
	CONTENT_LENGTH: CONTENT_LENGTH$1,
	CONTENT_LOCATION: CONTENT_LOCATION,
	CONTENT_MD5: CONTENT_MD5$1,
	CONTENT_RANGE: CONTENT_RANGE,
	CONTENT_TYPE: CONTENT_TYPE$1,
	DATE: DATE$1,
	ETAG: ETAG,
	EXPIRES: EXPIRES,
	LAST_MODIFIED: LAST_MODIFIED,
	LINK: LINK,
	LOCATION: LOCATION,
	P3P: P3P,
	PRAGMA: PRAGMA$1,
	PROXY_AUTHENTICATION: PROXY_AUTHENTICATION,
	PUBLIC_KEY_PINS: PUBLIC_KEY_PINS,
	REFRESH: REFRESH,
	RETRY_AFTER: RETRY_AFTER,
	SERVER: SERVER,
	SET_COOKIE: SET_COOKIE,
	STATUS: STATUS,
	STRICT_TRANSPORT_SECURITY: STRICT_TRANSPORT_SECURITY,
	TRAILER: TRAILER,
	TRANSFER_ENCODING: TRANSFER_ENCODING,
	UPGRADE: UPGRADE$1,
	VARY: VARY,
	VIA: VIA$1,
	WARNING: WARNING$1,
	WWW_AUTHENTICATE: WWW_AUTHENTICATE,
	X_FRAME_OPTIONS: X_FRAME_OPTIONS,
	X_XSS_PROTECTION: X_XSS_PROTECTION,
	CONTENT_SECURITY_POLICY: CONTENT_SECURITY_POLICY,
	X_CONTENT_TYPE_OPTIONS: X_CONTENT_TYPE_OPTIONS,
	X_POWERED_BY: X_POWERED_BY,
	X_UA_COMPATIBLE: X_UA_COMPATIBLE,
	X_CONTENT_DURATION: X_CONTENT_DURATION
});

/* global */

/**
 * @typedef {Object} CONSTSHEADERS
 */

/**
 * @type {CONSTSHEADERSREQUEST}
 */
const request = _request;

/**
 * @type {CONSTSHEADERSRESPONSE}
 */
const response = _response;

var _headers = /*#__PURE__*/Object.freeze({
	request: request,
	response: response
});

/* global */

/**
 * @typedef {Object} CONSTSMETHODS
 */

/**
 * The OPTIONS method represents a request for information about the communication options available on the
 * request/response chain identified by the Request-URI. This method allows the client to determine the options and/or
 * requirements associated with a resource, or the capabilities of a server, without implying a resource action or
 * initiating a resource retrieval.
 *
 * Responses to this method are not cacheable.
 *
 * If the OPTIONS request includes an entity-body (as indicated by the presence of Content-Length or
 * Transfer-Encoding), then the media type MUST be indicated by a Content-Type field. Although this specification does
 * not define any use for such a body, future extensions to HTTP might use the OPTIONS body to make more detailed
 * queries on the server. A server that does not support such an extension MAY discard the request body.
 *
 * If the Request-URI is an asterisk ("*"), the OPTIONS request is intended to apply to the server in general rather
 * than to a specific resource. Since a server's communication options typically depend on the resource, the "*"
 * request is only useful as a "ping" or "no-op" type of method, it does nothing beyond allowing the client to test the
 * capabilities of the server. For example, this can be used to test a proxy for HTTP/1.1 compliance (or lack thereof).
 *
 * If the Request-URI is not an asterisk, the OPTIONS request applies only to the options that are available when
 * communicating with that resource.
 *
 * A 200 response SHOULD include any header fields that indicate optional features implemented by the server and
 * applicable to that resource (e.g., Allow), possibly including extensions not defined by this specification. The
 * response body, if any, SHOULD also include information about the communication options. The format for such a body
 * is not defined by this specification, but might be defined by future extensions to HTTP. Content negotiation MAY be
 * used to select the appropriate response format. If no response body is included, the response MUST include a
 * Content-Length field with a field-value of "0".
 *
 * The Max-Forwards request-header field MAY be used to target a specific proxy in the request chain. When a proxy
 * receives an OPTIONS request on an absoluteURI for which request forwarding is permitted, the proxy MUST check for a
 * Max-Forwards field. If the Max-Forwards field-value is zero ("0"), the proxy MUST NOT forward the message, instead,
 * the proxy SHOULD respond with its own communication options. If the Max-Forwards field-value is an integer greater
 * than zero, the proxy MUST decrement the field-value when it forwards the request. If no Max-Forwards field is
 * present in the request, then the forwarded request MUST NOT include a Max-Forwards field.
 *
 * @type {String}
 */
const OPTIONS = 'OPTIONS';

/**
 * The GET method means retrieve whatever information (in the form of an entity) is identified by the Request-URI. If
 * the Request-URI refers to a data-producing process, it is the produced data which shall be returned as the entity in
 * the response and not the source text of the process, unless that text happens to be the output of the process.
 *
 * The semantics of the GET method change to a "conditional GET" if the request message includes an If-Modified-Since;
 * If-Unmodified-Since, If-Match, If-None-Match, or If-Range header field. A conditional GET method requests that the
 * entity be transferred only under the circumstances described by the conditional header field(s). The conditional GET
 * method is intended to reduce unnecessary network usage by allowing cached entities to be refreshed without requiring
 * multiple requests or transferring data already held by the client.
 *
 * The semantics of the GET method change to a "partial GET" if the request message includes a Range header field. A
 * partial GET requests that only part of the entity be transferred, as described in section 14.35. The partial GET
 * method is intended to reduce unnecessary network usage by allowing partially-retrieved entities to be completed
 * without transferring data already held by the client.
 *
 * The response to a GET request is cacheable if and only if it meets the requirements for HTTP caching described in
 * section 13.
 *
 * See section 15.1.3 for security considerations when used for forms.
 *
 * @type {String}
 */
const GET = 'GET';

/**
 * The HEAD method is identical to GET except that the server MUST NOT return a message-body in the response. The
 * metainformation contained in the HTTP headers in response to a HEAD request SHOULD be identical to the information
 * sent in response to a GET request. This method can be used for obtaining metainformation about the entity implied by
 * the request without transferring the entity-body itself. This method is often used for testing hypertext links for
 * validity, accessibility, and recent modification.
 *
 * The response to a HEAD request MAY be cacheable in the sense that the information contained in the response MAY be
 * used to update a previously cached entity from that resource. If the new field values indicate that the cached
 * entity differs from the current entity (as would be indicated by a change in Content-Length, Content-MD5, ETag or
 * Last-Modified), then the cache MUST treat the cache entry as stale.
 *
 * @type {String}
 */
const HEAD = 'HEAD';

/**
 * The POST method is used to request that the origin server accept the entity enclosed in the request as a new
 * subordinate of the resource identified by the Request-URI in the Request-Line. POST is designed to allow a uniform
 * method to cover the following functions:
 * <ul>
 *     <li>Annotation of existing resources,</li>
 *     <li>Posting a message to a bulletin board, newsgroup, mailing list, or similar group of articles,</li>
 *     <li>Providing a block of data, such as the result of submitting a form, to a data-handling process,</li>
 *     <li>Extending a database through an append operation.</li>
 * </ul>
 *
 * The actual function performed by the POST method is determined by the server and is usually dependent on the
 * Request-URI. The posted entity is subordinate to that URI in the same way that a file is subordinate to a directory
 * containing it, a news article is subordinate to a newsgroup to which it is posted, or a record is subordinate to a
 * database.
 *
 * The action performed by the POST method might not result in a resource that can be identified by a URI. In this
 * case, either 200 (OK) or 204 (No Content) is the appropriate response status, depending on whether or not the
 * response includes an entity that describes the result.
 *
 * If a resource has been created on the origin server, the response SHOULD be 201 (Created) and contain an entity
 * which describes the status of the request and refers to the new resource, and a Location header (see section 14.30).
 *
 * Responses to this method are not cacheable, unless the response includes appropriate Cache-Control or Expires header
 * fields. However, the 303 (See Other) response can be used to direct the user agent to retrieve a cacheable resource.
 *
 * POST requests MUST obey the message transmission requirements set out in section 8.2.
 *
 * See section 15.1.3 for security considerations.
 *
 * @type {String}
 */
const POST = 'POST';

/**
 * The PUT method requests that the enclosed entity be stored under the supplied Request-URI. If the Request-URI refers
 * to an already existing resource, the enclosed entity SHOULD be considered as a modified version of the one residing
 * on the origin server. If the Request-URI does not point to an existing resource, and that URI is capable of being
 * defined as a new resource by the requesting user agent, the origin server can create the resource with that URI. If
 * a new resource is created, the origin server MUST inform the user agent via the 201 (Created) response. If an
 * existing resource is modified, either the 200 (OK) or 204 (No Content) response codes SHOULD be sent to indicate
 * successful completion of the request. If the resource could not be created or modified with the Request-URI, an
 * appropriate error response SHOULD be given that reflects the nature of the problem. The recipient of the entity MUST
 * \NOT ignore any Content-* (e.g. Content-Range) headers that it does not understand or implement and MUST return a
 * 501 (Not Implemented) response in such cases.
 *
 * If the request passes through a cache and the Request-URI identifies one or more currently cached entities, those
 * entries SHOULD be treated as stale. Responses to this method are not cacheable.
 *
 * The fundamental difference between the POST and PUT requests is reflected in the different meaning of the
 * Request-URI. The URI in a POST request identifies the resource that will handle the enclosed entity. That resource
 * might be a data-accepting process, a gateway to some other protocol, or a separate entity that accepts annotations.
 * In contrast, the URI in a PUT request identifies the entity enclosed with the request -- the user agent knows what
 * URI is intended and the server MUST NOT attempt to apply the request to some other resource. If the server desires
 * that the request be applied to a different URI, it MUST send a 301 (Moved Permanently) response, the user agent MAY
 * then make its own decision regarding whether or not to redirect the request.
 *
 * A single resource MAY be identified by many different URIs. For example, an article might have a URI for identifying
 * "the current version" which is separate from the URI identifying each particular version. In this case, a PUT
 * request on a general URI might result in several other URIs being defined by the origin server.
 *
 * HTTP/1.1 does not define how a PUT method affects the state of an origin server.
 *
 * PUT requests MUST obey the message transmission requirements set out in section 8.2.
 *
 * Unless otherwise specified for a particular entity-header, the entity-headers in the PUT request SHOULD be applied
 * to the resource created or modified by the PUT.
 *
 * @type {String}
 */
const PUT = 'PUT';

/**
 * The DELETE method requests that the origin server delete the resource identified by the Request-URI. This method MAY
 * be overridden by human intervention (or other means) on the origin server. The client cannot be guaranteed that the
 * operation has been carried out, even if the status code returned from the origin server indicates that the action
 * has been completed successfully. However, the server SHOULD NOT indicate success unless, at the time the response
 * is given, it intends to delete the resource or move it to an inaccessible location.
 *
 * A successful response SHOULD be 200 (OK) if the response includes an entity describing the status, 202 (Accepted) if
 * the action has not yet been enacted, or 204 (No Content) if the action has been enacted but the response does not
 * include an entity.
 *
 * If the request passes through a cache and the Request-URI identifies one or more currently cached entities, those
 * entries SHOULD be treated as stale. Responses to this method are not cacheable.
 *
 * @type {String}
 */
const DELETE = 'DELETE';

/**
 * The TRACE method is used to invoke a remote, application-layer loop- back of the request message. The final
 * recipient of the request SHOULD reflect the message received back to the client as the entity-body of a 200 (OK)
 * response. The final recipient is either the origin server or the first proxy or gateway to receive a Max-Forwards
 * value of zero (0) in the request (see section 14.31). A TRACE request MUST NOT include an entity.
 *
 * TRACE allows the client to see what is being received at the other end of the request chain and use that data for
 * testing or diagnostic information. The value of the Via header field (section 14.45) is of particular interest,
 * since it acts as a trace of the request chain. Use of the Max-Forwards header field allows the client to limit the
 * length of the request chain, which is useful for testing a chain of proxies forwarding messages in an infinite loop.
 *
 * If the request is valid, the response SHOULD contain the entire request message in the entity-body, with a
 * Content-Type of "message/http". Responses to this method MUST NOT be cached.
 *
 * @type {String}
 */
const TRACE = 'TRACE';

/**
 * This specification reserves the method name CONNECT for use with a proxy that can dynamically switch to being a
 * tunnel (e.g. SSL tunneling [44]).
 *
 * @type {String}
 */
const CONNECT = 'CONNECT';

/**
 * The PATCH method requests that a set of changes described in the
 * request entity be applied to the resource identified by the Request-
 * URI.  The set of changes is represented in a format called a "patch
 * document" identified by a media type.  If the Request-URI does not
 * point to an existing resource, the server MAY create a new resource,
 * depending on the patch document type (whether it can logically modify
 * a null resource) and permissions, etc.
 * 
 * The difference between the PUT and PATCH requests is reflected in the
 * way the server processes the enclosed entity to modify the resource
 * identified by the Request-URI.  In a PUT request, the enclosed entity
 * is considered to be a modified version of the resource stored on the
 * origin server, and the client is requesting that the stored version
 * be replaced.  With PATCH, however, the enclosed entity contains a set
 * of instructions describing how a resource currently residing on the
 * origin server should be modified to produce a new version.  The PATCH
 * method affects the resource identified by the Request-URI, and it
 * also MAY have side effects on other resources; i.e., new resources
 * may be created, or existing ones modified, by the application of a
 * PATCH.
 * 
 * PATCH is neither safe nor idempotent as defined by [RFC2616], Section
 * 9.1.
 * 
 * A PATCH request can be issued in such a way as to be idempotent,
 * which also helps prevent bad outcomes from collisions between two
 * PATCH requests on the same resource in a similar time frame.
 * Collisions from multiple PATCH requests may be more dangerous than
 * PUT collisions because some patch formats need to operate from a
 * known base-point or else they will corrupt the resource.  Clients
 * using this kind of patch application SHOULD use a conditional request
 * such that the request will fail if the resource has been updated
 * since the client last accessed the resource.  For example, the client
 * can use a strong ETag [RFC2616] in an If-Match header on the PATCH
 * request.
 * 
 * There are also cases where patch formats do not need to operate from
 * a known base-point (e.g., appending text lines to log files, or non-
 * colliding rows to database tables), in which case the same care in
 * client requests is not needed.
 * 
 * The server MUST apply the entire set of changes atomically and never
 * provide (e.g., in response to a GET during this operation) a
 * partially modified representation.  If the entire patch document
 * cannot be successfully applied, then the server MUST NOT apply any of
 * the changes.  The determination of what constitutes a successful
 * PATCH can vary depending on the patch document and the type of
 * resource(s) being modified.  For example, the common 'diff' utility
 * can generate a patch document that applies to multiple files in a
 * directory hierarchy.  The atomicity requirement holds for all
 * directly affected files.  See "Error Handling", Section 2.2, for
 * details on status codes and possible error conditions.
 * 
 * If the request passes through a cache and the Request-URI identifies
 * one or more currently cached entities, those entries SHOULD be
 * treated as stale.  A response to this method is only cacheable if it
 * contains explicit freshness information (such as an Expires header or
 * "Cache-Control: max-age" directive) as well as the Content-Location
 * header matching the Request-URI, indicating that the PATCH response
 * body is a resource representation.  A cached PATCH response can only
 * be used to respond to subsequent GET and HEAD requests; it MUST NOT
 * be used to respond to other methods (in particular, PATCH).
 * 
 * Note that entity-headers contained in the request apply only to the
 * contained patch document and MUST NOT be applied to the resource
 * being modified.  Thus, a Content-Language header could be present on
 * the request, but it would only mean (for whatever that's worth) that
 * the patch document had a language.  Servers SHOULD NOT store such
 * headers except as trace information, and SHOULD NOT use such header
 * values the same way they might be used on PUT requests.  Therefore,
 * this document does not specify a way to modify a document's Content-
 * Type or Content-Language value through headers, though a mechanism
 * could well be designed to achieve this goal through a patch document.
 * 
 * There is no guarantee that a resource can be modified with PATCH.
 * Further, it is expected that different patch document formats will be
 * appropriate for different types of resources and that no single
 * format will be appropriate for all types of resources.  Therefore,
 * there is no single default patch document format that implementations
 * are required to support.  Servers MUST ensure that a received patch
 * document is appropriate for the type of resource identified by the
 * Request-URI.
 * 
 * Clients need to choose when to use PATCH rather than PUT.  For
 * example, if the patch document size is larger than the size of the
 * new resource data that would be used in a PUT, then it might make
 * sense to use PUT instead of PATCH.  A comparison to POST is even more
 * difficult, because POST is used in widely varying ways and can
 * encompass PUT and PATCH-like operations if the server chooses.  If
 * the operation does not modify the resource identified by the Request-
 * URI in a predictable way, POST should be considered instead of PATCH
 * or PUT.
 */
const PATCH = 'PATCH';

var _methods = /*#__PURE__*/Object.freeze({
	OPTIONS: OPTIONS,
	GET: GET,
	HEAD: HEAD,
	POST: POST,
	PUT: PUT,
	DELETE: DELETE,
	TRACE: TRACE,
	CONNECT: CONNECT,
	PATCH: PATCH
});

/* globals */

/**
 * @typedef {Object} CONSTSTYPES
 */

/**
 * 3D Crossword Plugin
 */
const x3d = 'application/vnd.hzn-3d-crossword';

/**
 * 3GP
 */
const _3gp = 'video/3gpp';

/**
 * 3GP2
 */
const _3g2 = 'video/3gpp2';

/**
 * 3GPP MSEQ File
 */
const mseq = 'application/vnd.mseq';

/**
 * 3M Post It Notes
 */
const pwn = 'application/vnd.3m.post-it-notes';

/**
 * 3rd Generation Partnership Project - Pic Large
 */
const plb = 'application/vnd.3gpp.pic-bw-large';

/**
 * 3rd Generation Partnership Project - Pic Small
 */
const psb = 'application/vnd.3gpp.pic-bw-small';

/**
 * 3rd Generation Partnership Project - Pic Var
 */
const pvb = 'application/vnd.3gpp.pic-bw-var';

/**
 * 3rd Generation Partnership Project - Transaction Capabilities Application Part
 */
const tcap = 'application/vnd.3gpp2.tcap';

/**
 * 7-Zip
 */
const _7z = 'application/x-7z-compressed';

/**
 * AbiWord
 */
const abw = 'application/x-abiword';

/**
 * Ace Archive
 */
const ace = 'application/x-ace-compressed';

/**
 * Active Content Compression
 */
const acc = 'application/vnd.americandynamics.acc';

/**
 * ACU Cobol
 */
const acu = 'application/vnd.acucobol';

/**
 * ACU Cobol
 */
const atc = 'application/vnd.acucorp';

/**
 * Adaptive differential pulse-code modulation
 */
const adp = 'audio/adpcm';

/**
 * Adobe (Macropedia) Authorware - Binary File
 */
const aab = 'application/x-authorware-bin';

/**
 * Adobe (Macropedia) Authorware - Map
 */
const aam = 'application/x-authorware-map';

/**
 * Adobe (Macropedia) Authorware - Segment File
 */
const aas = 'application/x-authorware-seg';

/**
 * Adobe AIR Application
 */
const air = 'application/vnd.adobe.air-application-installer-package+zip';

/**
 * Adobe Flash
 */
const swf = 'application/x-shockwave-flash';

/**
 * Adobe Flex Project
 */
const fxp = 'application/vnd.adobe.fxp';

/**
 * Adobe Portable Document Format
 */
const pdf = 'application/pdf';

/**
 * Adobe PostScript Printer Description File Format
 */
const ppd = 'application/vnd.cups-ppd';

/**
 * Adobe Shockwave Player
 */
const dir = 'application/x-director';

/**
 * Adobe XML Data Package
 */
const xdp = 'application/vnd.adobe.xdp+xml';

/**
 * Adobe XML Forms Data Format
 */
const xfdf = 'application/vnd.adobe.xfdf';

/**
 * Advanced Audio Coding (AAC)
 */
const aac = 'audio/x-aac';

/**
 * Ahead AIR Application
 */
const ahead = 'application/vnd.ahead.space';

/**
 * AirZip FileSECURE
 */
const azf = 'application/vnd.airzip.filesecure.azf';

/**
 * AirZip FileSECURE
 */
const azs = 'application/vnd.airzip.filesecure.azs';

/**
 * Amazon Kindle eBook format
 */
const azw = 'application/vnd.amazon.ebook';

/**
 * AmigaDE
 */
const ami = 'application/vnd.amiga.ami';

/**
 * Andrew Toolkit
 */
const atool = 'application/andrew-inset';

/**
 * Android Package Archive
 */
const apk = 'application/vnd.android.package-archive';

/**
 * ANSER-WEB Terminal Client - Certificate Issue
 */
const cii = 'application/vnd.anser-web-certificate-issue-initiation';

/**
 * ANSER-WEB Terminal Client - Web Funds Transfer
 */
const fti = 'application/vnd.anser-web-funds-transfer-initiation';

/**
 * Antix Game Player
 */
const atx = 'application/vnd.antix.game-component';

/**
 * Apple Installer Package
 */
const mpkg = 'application/vnd.apple.installer+xml';

/**
 * Applixware
 */
const aw = 'application/applixware';

/**
 * Archipelago Lesson Player
 */
const les = 'application/vnd.hhe.lesson-player';

/**
 * Arista Networks Software Image
 */
const swi = 'application/vnd.aristanetworks.swi';

/**
 * Assembler Source File
 */
const s = 'text/x-asm';

/**
 * Atom Publishing Protocol
 */
const atomcat = 'application/atomcat+xml';

/**
 * Atom Publishing Protocol Service Document
 */
const atomsvc = 'application/atomsvc+xml';

/**
 * Atom Syndication Format
 */
const atom = 'application/atom+xml';

/**
 * Attribute Certificate
 */
const ac = 'application/pkix-attr-cert';

/**
 * Audio Interchange File Format
 */
const aif = 'audio/x-aiff';

/**
 * Audio Video Interleave (AVI)
 */
const avi = 'video/x-msvideo';

/**
 * Audiograph
 */
const aep = 'application/vnd.audiograph';

/**
 * AutoCAD DXF
 */
const dxf = 'image/vnd.dxf';

/**
 * Autodesk Design Web Format (DWF)
 */
const dwf = 'model/vnd.dwf';

/**
 * BAS Partitur Format
 */
const par = 'text/plain-bas';

/**
 * Binary CPIO Archive
 */
const bcpio = 'application/x-bcpio';

/**
 * Binary Data
 */
const bin = 'application/octet-stream';

/**
 * BitTorrent
 */
const torrent = 'application/x-bittorrent';

/**
 * Blackberry COD File
 */
const cod = 'application/vnd.rim.cod';

/**
 * BMI Drawing Data Interchange
 */
const bmi = 'application/vnd.bmi';

/**
 * Bourne Shell Script
 */
const sh = 'application/x-sh';

/**
 * BTIF
 */
const btif = 'image/prs.btif';

/**
 * BusinessObjects
 */
const rep = 'application/vnd.businessobjects';

/**
 * Bzip Archive
 */
const bz = 'application/x-bzip';

/**
 * Bzip2 Archive
 */
const bz2 = 'application/x-bzip2';

/**
 * C Shell Script
 */
const csh = 'application/x-csh';

/**
 * C Source File
 */
const c = 'text/x-c';

/**
 * CambridgeSoft Chem Draw
 */
const cdxml = 'application/vnd.chemdraw+xml';

/**
 * Cascading Style Sheets (CSS)
 */
const css = 'text/css';

/**
 * ChemDraw eXchange file
 */
const cdx = 'chemical/x-cdx';

/**
 * Chemical Markup Language
 */
const cml = 'chemical/x-cml';

/**
 * Chemical Style Markup Language
 */
const csml = 'chemical/x-csml';

/**
 * CIM Database
 */
const cdbcmsg = 'application/vnd.contact.cmsg';

/**
 * Claymore Data Files
 */
const cla = 'application/vnd.claymore';

/**
 * Clonk Game
 */
const c4g = 'application/vnd.clonk.c4group';

/**
 * Close Captioning - Subtitle
 */
const sub = 'image/vnd.dvb.subtitle';

/**
 * Cloud Data Management Interface (CDMI) - Capability
 */
const cdmia = 'application/cdmi-capability';

/**
 * Cloud Data Management Interface (CDMI) - Contaimer
 */
const cdmic = 'application/cdmi-container';

/**
 * Cloud Data Management Interface (CDMI) - Domain
 */
const cdmid = 'application/cdmi-domain';

/**
 * Cloud Data Management Interface (CDMI) - Object
 */
const cdmio = 'application/cdmi-object';

/**
 * Cloud Data Management Interface (CDMI) - Queue
 */
const cdmiq = 'application/cdmi-queue';

/**
 * ClueTrust CartoMobile - Config
 */
const c11amc = 'application/vnd.cluetrust.cartomobile-config';

/**
 * ClueTrust CartoMobile - Config Package
 */
const c11amz = 'application/vnd.cluetrust.cartomobile-config-pkg';

/**
 * CMU Image
 */
const ras = 'image/x-cmu-raster';

/**
 * Comma-Seperated Values
 */
const csv = 'text/csv';

/**
 * Compact Pro
 */
const cpt = 'application/mac-compactpro';

/**
 * Compiled Wireless Markup Language (WMLC)
 */
const wmlc = 'application/vnd.wap.wmlc';

/**
 * Computer Graphics Metafile
 */
const cgm = 'image/cgm';

/**
 * CoolTalk
 */
const ice = 'x-conference/x-cooltalk';

/**
 * Corel Metafile Exchange (CMX)
 */
const cmx = 'image/x-cmx';

/**
 * CorelXARA
 */
const xar = 'application/vnd.xara';

/**
 * CosmoCaller
 */
const cmc = 'application/vnd.cosmocaller';

/**
 * CPIO Archive
 */
const cpio = 'application/x-cpio';

/**
 * CrickSoftware - Clicker
 */
const clkx = 'application/vnd.crick.clicker';

/**
 * CrickSoftware - Clicker - Keyboard
 */
const clkk = 'application/vnd.crick.clicker.keyboard';

/**
 * CrickSoftware - Clicker - Palette
 */
const clkp = 'application/vnd.crick.clicker.palette';

/**
 * CrickSoftware - Clicker - Template
 */
const clkt = 'application/vnd.crick.clicker.template';

/**
 * CrickSoftware - Clicker - Wordbank
 */
const clkw = 'application/vnd.crick.clicker.wordbank';

/**
 * Critical Tools - PERT Chart EXPERT
 */
const wbs = 'application/vnd.criticaltools.wbs+xml';

/**
 * CryptoNote
 */
const cryptonote = 'application/vnd.rig.cryptonote';

/**
 * Crystallographic Interchange Format
 */
const cif = 'chemical/x-cif';

/**
 * CrystalMaker Data Format
 */
const cmdf = 'chemical/x-cmdf';

/**
 * CU-SeeMe
 */
const cu = 'application/cu-seeme';

/**
 * CU-Writer
 */
const cww = 'application/prs.cww';

/**
 * Curl - Detached Applet
 */
const dcurl = 'text/vnd.curl.dcurl';

/**
 * Curl - Manifest File
 */
const mcurl = 'text/vnd.curl.mcurl';

/**
 * Curl - Source Code
 */
const scurl = 'text/vnd.curl.scurl';

/**
 * CURL Applet
 */
const car = 'application/vnd.curl.car';

/**
 * CURL Applet
 */
const pcurl = 'application/vnd.curl.pcurl';

/**
 * CustomMenu
 */
const cmp = 'application/vnd.yellowriver-custom-menu';

/**
 * Data Structure for the Security Suitability of Cryptographic Algorithms
 */
const dssc = 'application/dssc+der';

/**
 * Data Structure for the Security Suitability of Cryptographic Algorithms
 */
const xdssc = 'application/dssc+xml';

/**
 * Debian Package
 */
const deb = 'application/x-debian-package';

/**
 * DECE Audio
 */
const uva = 'audio/vnd.dece.audio';

/**
 * DECE Graphic
 */
const uvi = 'image/vnd.dece.graphic';

/**
 * DECE High Definition Video
 */
const uvh = 'video/vnd.dece.hd';

/**
 * DECE Mobile Video
 */
const uvm = 'video/vnd.dece.mobile';

/**
 * DECE MP4
 */
const uvu = 'video/vnd.uvvu.mp4';

/**
 * DECE PD Video
 */
const uvp = 'video/vnd.dece.pd';

/**
 * DECE SD Video
 */
const uvs = 'video/vnd.dece.sd';

/**
 * DECE Video
 */
const uvv = 'video/vnd.dece.video';

/**
 * Device Independent File Format (DVI)
 */
const dvi = 'application/x-dvi';

/**
 * Digital Siesmograph Networks - SEED Datafiles
 */
const seed = 'application/vnd.fdsn.seed';

/**
 * Digital Talking Book
 */
const dtb = 'application/x-dtbook+xml';

/**
 * Digital Talking Book - Resource File
 */
const res = 'application/x-dtbresource+xml';

/**
 * Digital Video Broadcasting
 */
const ait = 'application/vnd.dvb.ait';

/**
 * Digital Video Broadcasting
 */
const svc = 'application/vnd.dvb.service';

/**
 * Digital Winds Music
 */
const eol = 'audio/vnd.digital-winds';

/**
 * DjVu
 */
const djvu = 'image/vnd.djvu';

/**
 * Document Type Definition
 */
const dtd = 'application/xml-dtd';

/**
 * Dolby Meridian Lossless Packing
 */
const mlp = 'application/vnd.dolby.mlp';

/**
 * Doom Video Game
 */
const wad = 'application/x-doom';

/**
 * DPGraph
 */
const dpg = 'application/vnd.dpgraph';

/**
 * DRA Audio
 */
const dra = 'audio/vnd.dra';

/**
 * DreamFactory
 */
const dfac = 'application/vnd.dreamfactory';

/**
 * DTS Audio
 */
const dts = 'audio/vnd.dts';

/**
 * DTS High Definition Audio
 */
const dtshd = 'audio/vnd.dts.hd';

/**
 * DWG Drawing
 */
const dwg = 'image/vnd.dwg';

/**
 * DynaGeo
 */
const geo = 'application/vnd.dynageo';

/**
 * ECMAScript
 */
const es = 'application/ecmascript';

/**
 * EcoWin Chart
 */
const mag = 'application/vnd.ecowin.chart';

/**
 * EDMICS 2000
 */
const mmr = 'image/vnd.fujixerox.edmics-mmr';

/**
 * EDMICS 2000
 */
const rlc = 'image/vnd.fujixerox.edmics-rlc';

/**
 * Efficient XML Interchange
 */
const exi = 'application/exi';

/**
 * EFI Proteus
 */
const mgz = 'application/vnd.proteus.magazine';

/**
 * Electronic Publication
 */
const epub = 'application/epub+zip';

/**
 * Email Message
 */
const eml = 'message/rfc822';

/**
 * Enliven Viewer
 */
const nml = 'application/vnd.enliven';

/**
 * Express by Infoseek
 */
const xpr = 'application/vnd.is-xpr';

/**
 * eXtended Image File Format (XIFF)
 */
const xif = 'image/vnd.xiff';

/**
 * Extensible Forms Description Language
 */
const xfdl = 'application/vnd.xfdl';

/**
 * Extensible MultiModal Annotation
 */
const emma = 'application/emma+xml';

/**
 * EZPix Secure Photo Album
 */
const ez2 = 'application/vnd.ezpix-album';

/**
 * EZPix Secure Photo Album
 */
const ez3 = 'application/vnd.ezpix-package';

/**
 * FAST Search & Transfer ASA
 */
const fst = 'image/vnd.fst';

/**
 * FAST Search & Transfer ASA
 */
const fvt = 'video/vnd.fvt';

/**
 * FastBid Sheet
 */
const fbs = 'image/vnd.fastbidsheet';

/**
 * FCS Express Layout Link
 */
const fe_launch = 'application/vnd.denovo.fcselayout-link';

/**
 * Flash Video
 */
const f4v = 'video/x-f4v';

/**
 * Flash Video
 */
const flv = 'video/x-flv';

/**
 * FlashPix
 */
const fpx = 'image/vnd.fpx';

/**
 * FlashPix
 */
const npx = 'image/vnd.net-fpx';

/**
 * FLEXSTOR
 */
const flx = 'text/vnd.fmi.flexstor';

/**
 * FLI/FLC Animation Format
 */
const fli = 'video/x-fli';

/**
 * FluxTime Clip
 */
const ftc = 'application/vnd.fluxtime.clip';

/**
 * Forms Data Format
 */
const fdf = 'application/vnd.fdf';

/**
 * Fortran Source File
 */
const f = 'text/x-fortran';

/**
 * FrameMaker Interchange Format
 */
const mif = 'application/vnd.mif';

/**
 * FrameMaker Normal Format
 */
const fm = 'application/vnd.framemaker';

/**
 * FreeHand MX
 */
const fh = 'image/x-freehand';

/**
 * Friendly Software Corporation
 */
const fsc = 'application/vnd.fsc.weblaunch';

/**
 * Frogans Player
 */
const fnc = 'application/vnd.frogans.fnc';

/**
 * Frogans Player
 */
const ltf = 'application/vnd.frogans.ltf';

/**
 * Fujitsu - Xerox 2D CAD Data
 */
const ddd = 'application/vnd.fujixerox.ddd';

/**
 * Fujitsu - Xerox DocuWorks
 */
const xdw = 'application/vnd.fujixerox.docuworks';

/**
 * Fujitsu - Xerox DocuWorks Binder
 */
const xbd = 'application/vnd.fujixerox.docuworks.binder';

/**
 * Fujitsu Oasys
 */
const oas = 'application/vnd.fujitsu.oasys';

/**
 * Fujitsu Oasys
 */
const oa2 = 'application/vnd.fujitsu.oasys2';

/**
 * Fujitsu Oasys
 */
const oa3 = 'application/vnd.fujitsu.oasys3';

/**
 * Fujitsu Oasys
 */
const fg5 = 'application/vnd.fujitsu.oasysgp';

/**
 * Fujitsu Oasys
 */
const bh2 = 'application/vnd.fujitsu.oasysprs';

/**
 * FutureSplash Animator
 */
const spl = 'application/x-futuresplash';

/**
 * FuzzySheet
 */
const fzs = 'application/vnd.fuzzysheet';

/**
 * G3 Fax Image
 */
const g3 = 'image/g3fax';

/**
 * GameMaker ActiveX
 */
const gmx = 'application/vnd.gmx';

/**
 * Gen-Trix Studio
 */
const gtw = 'model/vnd.gtw';

/**
 * Genomatix Tuxedo Framework
 */
const txd = 'application/vnd.genomatix.tuxedo';

/**
 * GeoGebra
 */
const ggb = 'application/vnd.geogebra.file';

/**
 * GeoGebra
 */
const ggt = 'application/vnd.geogebra.tool';

/**
 * Geometric Description Language (GDL)
 */
const gdl = 'model/vnd.gdl';

/**
 * GeoMetry Explorer
 */
const gex = 'application/vnd.geometry-explorer';

/**
 * GEONExT and JSXGraph
 */
const gxt = 'application/vnd.geonext';

/**
 * GeoplanW
 */
const g2w = 'application/vnd.geoplan';

/**
 * GeospacW
 */
const g3w = 'application/vnd.geospace';

/**
 * Ghostscript Font
 */
const gsf = 'application/x-font-ghostscript';

/**
 * Glyph Bitmap Distribution Format
 */
const bdf = 'application/x-font-bdf';

/**
 * GNU Tar Files
 */
const gtar = 'application/x-gtar';

/**
 * GNU Texinfo Document
 */
const texinfo = 'application/x-texinfo';

/**
 * Gnumeric
 */
const gnumeric = 'application/x-gnumeric';

/**
 * Google Earth - KML
 */
const kml = 'application/vnd.google-earth.kml+xml';

/**
 * Google Earth - Zipped KML
 */
const kmz = 'application/vnd.google-earth.kmz';

/**
 * GrafEq
 */
const gqf = 'application/vnd.grafeq';

/**
 * Graphics Interchange Format
 */
const gif = 'image/gif';

/**
 * Graphviz
 */
const gv = 'text/vnd.graphviz';

/**
 * Groove - Account
 */
const gac = 'application/vnd.groove-account';

/**
 * Groove - Help
 */
const ghf = 'application/vnd.groove-help';

/**
 * Groove - Identity Message
 */
const gim = 'application/vnd.groove-identity-message';

/**
 * Groove - Injector
 */
const grv = 'application/vnd.groove-injector';

/**
 * Groove - Tool Message
 */
const gtm = 'application/vnd.groove-tool-message';

/**
 * Groove - Tool Template
 */
const tpl = 'application/vnd.groove-tool-template';

/**
 * Groove - Vcard
 */
const vcg = 'application/vnd.groove-vcard';

/**
 * H.261
 */
const h261 = 'video/h261';

/**
 * H.263
 */
const h263 = 'video/h263';

/**
 * H.264
 */
const h264 = 'video/h264';

/**
 * Hewlett Packard Instant Delivery
 */
const hpid = 'application/vnd.hp-hpid';

/**
 * Hewlett-Packard's WebPrintSmart
 */
const hps = 'application/vnd.hp-hps';

/**
 * Hierarchical Data Format
 */
const hdf = 'application/x-hdf';

/**
 * Hit'n'Mix
 */
const rip = 'audio/vnd.rip';

/**
 * Homebanking Computer Interface (HBCI)
 */
const hbci = 'application/vnd.hbci';

/**
 * HP Indigo Digital Press - Job Layout Languate
 */
const jlt = 'application/vnd.hp-jlyt';

/**
 * HP Printer Command Language
 */
const pcl = 'application/vnd.hp-pcl';

/**
 * HP-GL/2 and HP RTL
 */
const hpgl = 'application/vnd.hp-hpgl';

/**
 * HV Script
 */
const hvs = 'application/vnd.yamaha.hv-script';

/**
 * HV Voice Dictionary
 */
const hvd = 'application/vnd.yamaha.hv-dic';

/**
 * HV Voice Parameter
 */
const hvp = 'application/vnd.yamaha.hv-voice';

/**
 * Hydrostatix Master Suite
 */
const sfd_hdstx = 'application/vnd.hydrostatix.sof-data';

/**
 * Hyperstudio
 */
const stk = 'application/hyperstudio';

/**
 * Hypertext Application Language
 */
const hal = 'application/vnd.hal+xml';

/**
 * HyperText Markup Language (HTML)
 */
const html = 'text/html';

/**
 * IBM DB2 Rights Manager
 */
const irm = 'application/vnd.ibm.rights-management';

/**
 * IBM Electronic Media Management System - Secure Container
 */
const sc = 'application/vnd.ibm.secure-container';

/**
 * iCalendar
 */
const ics = 'text/calendar';

/**
 * ICC profile
 */
const icc = 'application/vnd.iccprofile';

/**
 * Icon Image
 */
const ico = 'image/x-icon';

/**
 * igLoader
 */
const igl = 'application/vnd.igloader';

/**
 * Image Exchange Format
 */
const ief = 'image/ief';

/**
 * ImmerVision PURE Players
 */
const ivp = 'application/vnd.immervision-ivp';

/**
 * ImmerVision PURE Players
 */
const ivu = 'application/vnd.immervision-ivu';

/**
 * IMS Networks
 */
const rif = 'application/reginfo+xml';

/**
 * In3D - 3DML
 */
const spot = 'text/vnd.in3d.spot';

/**
 * Initial Graphics Exchange Specification (IGES)
 */
const igs = 'model/iges';

/**
 * Interactive Geometry Software
 */
const i2g = 'application/vnd.intergeo';

/**
 * Interactive Geometry Software Cinderella
 */
const cdy = 'application/vnd.cinderella';

/**
 * Intercon FormNet
 */
const xpw = 'application/vnd.intercon.formnet';

/**
 * International Society for Advancement of Cytometry
 */
const fcs = 'application/vnd.isac.fcs';

/**
 * Internet Protocol Flow Information Export
 */
const ipfix = 'application/ipfix';

/**
 * Internet Public Key Infrastructure - Certificate
 */
const cer = 'application/pkix-cert';

/**
 * Internet Public Key Infrastructure - Certificate Management Protocole
 */
const pki = 'application/pkixcmp';

/**
 * Internet Public Key Infrastructure - Certificate Revocation Lists
 */
const crl = 'application/pkix-crl';

/**
 * Internet Public Key Infrastructure - Certification Path
 */
const pkipath = 'application/pkix-pkipath';

/**
 * IOCOM Visimeet
 */
const igm = 'application/vnd.insors.igm';

/**
 * IP Unplugged Roaming Client
 */
const rcprofile = 'application/vnd.ipunplugged.rcprofile';

/**
 * iRepository / Lucidoc Editor
 */
const irp = 'application/vnd.irepository.package+xml';

/**
 * J2ME App Descriptor
 */
const jad = 'text/vnd.sun.j2me.app-descriptor';

/**
 * Java Archive
 */
const jar = 'application/java-archive';

/**
 * Java Bytecode File
 */
const _class = 'application/java-vm';

/**
 * Java Network Launching Protocol
 */
const jnlp = 'application/x-java-jnlp-file';

/**
 * Java Serialized Object
 */
const ser = 'application/java-serialized-object';

/**
 * Java Source File
 */
const java = 'text/x-java-source,java';

/**
 * JavaScript
 */
const js = 'application/javascript';

/**
 * JavaScript Object Notation (JSON)
 */
const json = 'application/json';

/**
 * Joda Archive
 */
const joda = 'application/vnd.joost.joda-archive';

/**
 * JPEG 2000 Compound Image File Format
 */
const jpm = 'video/jpm';

/**
 * JPEG Image
 */
const jpeg = 'image/jpeg';

/**
 * JPGVideo
 */
const jpgv = 'video/jpeg';

/**
 * Kahootz
 */
const ktz = 'application/vnd.kahootz';

/**
 * Karaoke on Chipnuts Chipsets
 */
const mmd = 'application/vnd.chipnuts.karaoke-mmd';

/**
 * KDE KOffice Office Suite - Karbon
 */
const karbon = 'application/vnd.kde.karbon';

/**
 * KDE KOffice Office Suite - KChart
 */
const chrt = 'application/vnd.kde.kchart';

/**
 * KDE KOffice Office Suite - Kformula
 */
const kfo = 'application/vnd.kde.kformula';

/**
 * KDE KOffice Office Suite - Kivio
 */
const flw = 'application/vnd.kde.kivio';

/**
 * KDE KOffice Office Suite - Kontour
 */
const kon = 'application/vnd.kde.kontour';

/**
 * KDE KOffice Office Suite - Kpresenter
 */
const kpr = 'application/vnd.kde.kpresenter';

/**
 * KDE KOffice Office Suite - Kspread
 */
const ksp = 'application/vnd.kde.kspread';

/**
 * KDE KOffice Office Suite - Kword
 */
const kwd = 'application/vnd.kde.kword';

/**
 * Kenamea App
 */
const htke = 'application/vnd.kenameaapp';

/**
 * Kidspiration
 */
const kia = 'application/vnd.kidspiration';

/**
 * Kinar Applications
 */
const kne = 'application/vnd.kinar';

/**
 * Kodak Storyshare
 */
const sse = 'application/vnd.kodak-descriptor';

/**
 * Laser App Enterprise
 */
const lasxml = 'application/vnd.las.las+xml';

/**
 * LaTeX
 */
const latex = 'application/x-latex';

/**
 * Life Balance - Desktop Edition
 */
const lbd = 'application/vnd.llamagraphics.life-balance.desktop';

/**
 * Life Balance - Exchange Format
 */
const lbe = 'application/vnd.llamagraphics.life-balance.exchange+xml';

/**
 * Lightspeed Audio Lab
 */
const jam = 'application/vnd.jam';

/**
 * Lotus 1-2-3
 */
const _123 = 'application/vnd.lotus-1-2-3';

/**
 * Lotus Approach
 */
const apr = 'application/vnd.lotus-approach';

/**
 * Lotus Freelance
 */
const pre = 'application/vnd.lotus-freelance';

/**
 * Lotus Notes
 */
const nsf = 'application/vnd.lotus-notes';

/**
 * Lotus Organizer
 */
const org = 'application/vnd.lotus-organizer';

/**
 * Lotus Screencam
 */
const scm = 'application/vnd.lotus-screencam';

/**
 * Lotus Wordpro
 */
const lwp = 'application/vnd.lotus-wordpro';

/**
 * Lucent Voice
 */
const lvp = 'audio/vnd.lucent.voice';

/**
 * M3U (Multimedia Playlist)
 */
const m3u = 'audio/x-mpegurl';

/**
 * M4v
 */
const m4v = 'video/x-m4v';

/**
 * Macintosh BinHex 4.0
 */
const hqx = 'application/mac-binhex40';

/**
 * MacPorts Port System
 */
const portpkg = 'application/vnd.macports.portpkg';

/**
 * MapGuide DBXML
 */
const mgp = 'application/vnd.osgeo.mapguide.package';

/**
 * MARC Formats
 */
const mrc = 'application/marc';

/**
 * MARC21 XML Schema
 */
const mrcx = 'application/marcxml+xml';

/**
 * Material Exchange Format
 */
const mxf = 'application/mxf';

/**
 * Mathematica Notebook Player
 */
const nbp = 'application/vnd.wolfram.player';

/**
 * Mathematica Notebooks
 */
const ma = 'application/mathematica';

/**
 * Mathematical Markup Language
 */
const mathml = 'application/mathml+xml';

/**
 * Mbox database files
 */
const mbox = 'application/mbox';

/**
 * MedCalc
 */
const mc1 = 'application/vnd.medcalcdata';

/**
 * Media Server Control Markup Language
 */
const mscml = 'application/mediaservercontrol+xml';

/**
 * MediaRemote
 */
const cdkey = 'application/vnd.mediastation.cdkey';

/**
 * Medical Waveform Encoding Format
 */
const mwf = 'application/vnd.mfer';

/**
 * Melody Format for Mobile Platform
 */
const mfm = 'application/vnd.mfmp';

/**
 * Mesh Data Type
 */
const msh = 'model/mesh';

/**
 * Metadata Authority Description Schema
 */
const mads = 'application/mads+xml';

/**
 * Metadata Encoding and Transmission Standard
 */
const mets = 'application/mets+xml';

/**
 * Metadata Object Description Schema
 */
const mods = 'application/mods+xml';

/**
 * Metalink
 */
const meta4 = 'application/metalink4+xml';

/**
 * Micosoft PowerPoint - Macro-Enabled Template File
 */
const potm = 'application/vnd.ms-powerpoint.template.macroenabled.12';

/**
 * Micosoft Word - Macro-Enabled Document
 */
const docm = 'application/vnd.ms-word.document.macroenabled.12';

/**
 * Micosoft Word - Macro-Enabled Template
 */
const dotm = 'application/vnd.ms-word.template.macroenabled.12';

/**
 * Micro CADAM Helix D&D
 */
const mcd = 'application/vnd.mcd';

/**
 * Micrografx
 */
const flo = 'application/vnd.micrografx.flo';

/**
 * Micrografx iGrafx Professional
 */
const igx = 'application/vnd.micrografx.igx';

/**
 * MICROSEC e-Szign
 */
const es3 = 'application/vnd.eszigno3+xml';

/**
 * Microsoft Access
 */
const mdb = 'application/x-msaccess';

/**
 * Microsoft Advanced Systems Format (ASF)
 */
const asf = 'video/x-ms-asf';

/**
 * Microsoft Application
 */
const exe = 'application/x-msdownload';

/**
 * Microsoft Artgalry
 */
const cil = 'application/vnd.ms-artgalry';

/**
 * Microsoft Cabinet File
 */
const cab = 'application/vnd.ms-cab-compressed';

/**
 * Microsoft Class Server
 */
const ims = 'application/vnd.ms-ims';

/**
 * Microsoft ClickOnce
 */
const application = 'application/x-ms-application';

/**
 * Microsoft Clipboard Clip
 */
const clp = 'application/x-msclip';

/**
 * Microsoft Document Imaging Format
 */
const mdi = 'image/vnd.ms-modi';

/**
 * Microsoft Embedded OpenType
 */
const eot = 'application/vnd.ms-fontobject';

/**
 * Microsoft Excel
 */
const xls = 'application/vnd.ms-excel';

/**
 * Microsoft Excel - Add-In File
 */
const xlam = 'application/vnd.ms-excel.addin.macroenabled.12';

/**
 * Microsoft Excel - Binary Workbook
 */
const xlsb = 'application/vnd.ms-excel.sheet.binary.macroenabled.12';

/**
 * Microsoft Excel - Macro-Enabled Template File
 */
const xltm = 'application/vnd.ms-excel.template.macroenabled.12';

/**
 * Microsoft Excel - Macro-Enabled Workbook
 */
const xlsm = 'application/vnd.ms-excel.sheet.macroenabled.12';

/**
 * Microsoft Html Help File
 */
const chm = 'application/vnd.ms-htmlhelp';

/**
 * Microsoft Information Card
 */
const crd = 'application/x-mscardfile';

/**
 * Microsoft Learning Resource Module
 */
const lrm = 'application/vnd.ms-lrm';

/**
 * Microsoft MediaView
 */
const mvb = 'application/x-msmediaview';

/**
 * Microsoft Money
 */
const mny = 'application/x-msmoney';

/**
 * Microsoft Office - OOXML - Presentation
 */
const pptx = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';

/**
 * Microsoft Office - OOXML - Presentation (Slide)
 */
const sldx = 'application/vnd.openxmlformats-officedocument.presentationml.slide';

/**
 * Microsoft Office - OOXML - Presentation (Slideshow)
 */
const ppsx = 'application/vnd.openxmlformats-officedocument.presentationml.slideshow';

/**
 * Microsoft Office - OOXML - Presentation Template
 */
const potx = 'application/vnd.openxmlformats-officedocument.presentationml.template';

/**
 * Microsoft Office - OOXML - Spreadsheet
 */
const xlsx = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Microsoft Office - OOXML - Spreadsheet Teplate
 */
const xltx = 'application/vnd.openxmlformats-officedocument.spreadsheetml.template';

/**
 * Microsoft Office - OOXML - Word Document
 */
const docx = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * Microsoft Office - OOXML - Word Document Template
 */
const dotx = 'application/vnd.openxmlformats-officedocument.wordprocessingml.template';

/**
 * Microsoft Office Binder
 */
const obd = 'application/x-msbinder';

/**
 * Microsoft Office System Release Theme
 */
const thmx = 'application/vnd.ms-officetheme';

/**
 * Microsoft OneNote
 */
const onetoc = 'application/onenote';

/**
 * Microsoft PlayReady Ecosystem
 */
const pya = 'audio/vnd.ms-playready.media.pya';

/**
 * Microsoft PlayReady Ecosystem Video
 */
const pyv = 'video/vnd.ms-playready.media.pyv';

/**
 * Microsoft PowerPoint
 */
const ppt = 'application/vnd.ms-powerpoint';

/**
 * Microsoft PowerPoint - Add-in file
 */
const ppam = 'application/vnd.ms-powerpoint.addin.macroenabled.12';

/**
 * Microsoft PowerPoint - Macro-Enabled Open XML Slide
 */
const sldm = 'application/vnd.ms-powerpoint.slide.macroenabled.12';

/**
 * Microsoft PowerPoint - Macro-Enabled Presentation File
 */
const pptm = 'application/vnd.ms-powerpoint.presentation.macroenabled.12';

/**
 * Microsoft PowerPoint - Macro-Enabled Slide Show File
 */
const ppsm = 'application/vnd.ms-powerpoint.slideshow.macroenabled.12';

/**
 * Microsoft Project
 */
const mpp = 'application/vnd.ms-project';

/**
 * Microsoft Publisher
 */
const pub = 'application/x-mspublisher';

/**
 * Microsoft Schedule+
 */
const scd = 'application/x-msschedule';

/**
 * Microsoft Silverlight
 */
const xap = 'application/x-silverlight-app';

/**
 * Microsoft Trust UI Provider - Certificate Trust Link
 */
const stl = 'application/vnd.ms-pki.stl';

/**
 * Microsoft Trust UI Provider - Security Catalog
 */
const cat = 'application/vnd.ms-pki.seccat';

/**
 * Microsoft Visio
 */
const vsd = 'application/vnd.visio';

/**
 * Microsoft Windows Media
 */
const wm = 'video/x-ms-wm';

/**
 * Microsoft Windows Media Audio
 */
const wma = 'audio/x-ms-wma';

/**
 * Microsoft Windows Media Audio Redirector
 */
const wax = 'audio/x-ms-wax';

/**
 * Microsoft Windows Media Audio/Video Playlist
 */
const wmx = 'video/x-ms-wmx';

/**
 * Microsoft Windows Media Player Download Package
 */
const wmd = 'application/x-ms-wmd';

/**
 * Microsoft Windows Media Player Playlist
 */
const wpl = 'application/vnd.ms-wpl';

/**
 * Microsoft Windows Media Player Skin Package
 */
const wmz = 'application/x-ms-wmz';

/**
 * Microsoft Windows Media Video
 */
const wmv = 'video/x-ms-wmv';

/**
 * Microsoft Windows Media Video Playlist
 */
const wvx = 'video/x-ms-wvx';

/**
 * Microsoft Windows Metafile
 */
const wmf = 'application/x-msmetafile';

/**
 * Microsoft Windows Terminal Services
 */
const trm = 'application/x-msterminal';

/**
 * Microsoft Word
 */
const doc = 'application/msword';

/**
 * Microsoft Wordpad
 */
const wri = 'application/x-mswrite';

/**
 * Microsoft Works
 */
const wps = 'application/vnd.ms-works';

/**
 * Microsoft XAML Browser Application
 */
const xbap = 'application/x-ms-xbap';

/**
 * Microsoft XML Paper Specification
 */
const xps = 'application/vnd.ms-xpsdocument';

/**
 * MIDI - Musical Instrument Digital Interface
 */
const mid = 'audio/midi';

/**
 * MiniPay
 */
const mpy = 'application/vnd.ibm.minipay';

/**
 * MO:DCA-P
 */
const afp = 'application/vnd.ibm.modcap';

/**
 * Mobile Information Device Profile
 */
const rms = 'application/vnd.jcp.javame.midlet-rms';

/**
 * MobileTV
 */
const tmo = 'application/vnd.tmobile-livetv';

/**
 * Mobipocket
 */
const prc = 'application/x-mobipocket-ebook';

/**
 * Mobius Management Systems - Basket file
 */
const mbk = 'application/vnd.mobius.mbk';

/**
 * Mobius Management Systems - Distribution Database
 */
const dis = 'application/vnd.mobius.dis';

/**
 * Mobius Management Systems - Policy Definition Language File
 */
const plc = 'application/vnd.mobius.plc';

/**
 * Mobius Management Systems - Query File
 */
const mqy = 'application/vnd.mobius.mqy';

/**
 * Mobius Management Systems - Script Language
 */
const msl = 'application/vnd.mobius.msl';

/**
 * Mobius Management Systems - Topic Index File
 */
const txf = 'application/vnd.mobius.txf';

/**
 * Mobius Management Systems - UniversalArchive
 */
const daf = 'application/vnd.mobius.daf';

/**
 * mod_fly / fly.cgi
 */
const fly = 'text/vnd.fly';

/**
 * Mophun Certificate
 */
const mpc = 'application/vnd.mophun.certificate';

/**
 * Mophun VM
 */
const mpn = 'application/vnd.mophun.application';

/**
 * Motion JPEG 2000
 */
const mj2 = 'video/mj2';

/**
 * MPEG Audio
 */
const mpga = 'audio/mpeg';

/**
 * MPEG Url
 */
const mxu = 'video/vnd.mpegurl';

/**
 * MPEG Video
 */
const mpeg = 'video/mpeg';

/**
 * MPEG-21
 */
const m21 = 'application/mp21';

/**
 * MPEG-4 Audio
 */
const mp4a = 'audio/mp4';

/**
 * MPEG-4 Video
 */
const _mp4 = 'video/mp4';

/**
 * MPEG4
 */
const mp4 = 'application/mp4';

/**
 * Multimedia Playlist Unicode
 */
const m3u8 = 'application/vnd.apple.mpegurl';

/**
 * MUsical Score Interpreted Code Invented for the ASCII designation of Notation
 */
const mus = 'application/vnd.musician';

/**
 * Muvee Automatic Video Editing
 */
const msty = 'application/vnd.muvee.style';

/**
 * MXML
 */
const mxml = 'application/xv+xml';

/**
 * N-Gage Game Data
 */
const ngdat = 'application/vnd.nokia.n-gage.data';

/**
 * N-Gage Game Installer
 */
const n_gage = 'application/vnd.nokia.n-gage.symbian.install';

/**
 * Navigation Control file for XML (for ePub)
 */
const ncx = 'application/x-dtbncx+xml';

/**
 * Network Common Data Form (NetCDF)
 */
const nc = 'application/x-netcdf';

/**
 * neuroLanguage
 */
const nlu = 'application/vnd.neurolanguage.nlu';

/**
 * New Moon Liftoff/DNA
 */
const dna = 'application/vnd.dna';

/**
 * NobleNet Directory
 */
const nnd = 'application/vnd.noblenet-directory';

/**
 * NobleNet Sealer
 */
const nns = 'application/vnd.noblenet-sealer';

/**
 * NobleNet Web
 */
const nnw = 'application/vnd.noblenet-web';

/**
 * Nokia Radio Application - Preset
 */
const rpst = 'application/vnd.nokia.radio-preset';

/**
 * Nokia Radio Application - Preset
 */
const rpss = 'application/vnd.nokia.radio-presets';

/**
 * Notation3
 */
const n3 = 'text/n3';

/**
 * Novadigm's RADIA and EDM products
 */
const edm = 'application/vnd.novadigm.edm';

/**
 * Novadigm's RADIA and EDM products
 */
const edx = 'application/vnd.novadigm.edx';

/**
 * Novadigm's RADIA and EDM products
 */
const ext = 'application/vnd.novadigm.ext';

/**
 * NpGraphIt
 */
const gph = 'application/vnd.flographit';

/**
 * Nuera ECELP 4800
 */
const ecelp4800 = 'audio/vnd.nuera.ecelp4800';

/**
 * Nuera ECELP 7470
 */
const ecelp7470 = 'audio/vnd.nuera.ecelp7470';

/**
 * Nuera ECELP 9600
 */
const ecelp9600 = 'audio/vnd.nuera.ecelp9600';

/**
 * Office Document Architecture
 */
const oda = 'application/oda';

/**
 * Ogg
 */
const ogx = 'application/ogg';

/**
 * Ogg Audio
 */
const oga = 'audio/ogg';

/**
 * Ogg Video
 */
const ogv = 'video/ogg';

/**
 * OMA Download Agents
 */
const dd2 = 'application/vnd.oma.dd2+xml';

/**
 * Open Document Text Web
 */
const oth = 'application/vnd.oasis.opendocument.text-web';

/**
 * Open eBook Publication Structure
 */
const opf = 'application/oebps-package+xml';

/**
 * Open Financial Exchange
 */
const qbo = 'application/vnd.intu.qbo';

/**
 * Open Office Extension
 */
const oxt = 'application/vnd.openofficeorg.extension';

/**
 * Open Score Format
 */
const osf = 'application/vnd.yamaha.openscoreformat';

/**
 * Open Web Media Project - Audio
 */
const weba = 'audio/webm';

/**
 * Open Web Media Project - Video
 */
const webm = 'video/webm';

/**
 * OpenDocument Chart
 */
const odc = 'application/vnd.oasis.opendocument.chart';

/**
 * OpenDocument Chart Template
 */
const otc = 'application/vnd.oasis.opendocument.chart-template';

/**
 * OpenDocument Database
 */
const odb = 'application/vnd.oasis.opendocument.database';

/**
 * OpenDocument Formula
 */
const odf = 'application/vnd.oasis.opendocument.formula';

/**
 * OpenDocument Formula Template
 */
const odft = 'application/vnd.oasis.opendocument.formula-template';

/**
 * OpenDocument Graphics
 */
const odg = 'application/vnd.oasis.opendocument.graphics';

/**
 * OpenDocument Graphics Template
 */
const otg = 'application/vnd.oasis.opendocument.graphics-template';

/**
 * OpenDocument Image
 */
const odi = 'application/vnd.oasis.opendocument.image';

/**
 * OpenDocument Image Template
 */
const oti = 'application/vnd.oasis.opendocument.image-template';

/**
 * OpenDocument Presentation
 */
const odp = 'application/vnd.oasis.opendocument.presentation';

/**
 * OpenDocument Presentation Template
 */
const otp = 'application/vnd.oasis.opendocument.presentation-template';

/**
 * OpenDocument Spreadsheet
 */
const ods = 'application/vnd.oasis.opendocument.spreadsheet';

/**
 * OpenDocument Spreadsheet Template
 */
const ots = 'application/vnd.oasis.opendocument.spreadsheet-template';

/**
 * OpenDocument Text
 */
const odt = 'application/vnd.oasis.opendocument.text';

/**
 * OpenDocument Text Master
 */
const odm = 'application/vnd.oasis.opendocument.text-master';

/**
 * OpenDocument Text Template
 */
const ott = 'application/vnd.oasis.opendocument.text-template';

/**
 * OpenGL Textures (KTX)
 */
const ktx = 'image/ktx';

/**
 * OpenOffice - Calc (Spreadsheet)
 */
const sxc = 'application/vnd.sun.xml.calc';

/**
 * OpenOffice - Calc Template (Spreadsheet)
 */
const stc = 'application/vnd.sun.xml.calc.template';

/**
 * OpenOffice - Draw (Graphics)
 */
const sxd = 'application/vnd.sun.xml.draw';

/**
 * OpenOffice - Draw Template (Graphics)
 */
const std = 'application/vnd.sun.xml.draw.template';

/**
 * OpenOffice - Impress (Presentation)
 */
const sxi = 'application/vnd.sun.xml.impress';

/**
 * OpenOffice - Impress Template (Presentation)
 */
const sti = 'application/vnd.sun.xml.impress.template';

/**
 * OpenOffice - Math (Formula)
 */
const sxm = 'application/vnd.sun.xml.math';

/**
 * OpenOffice - Writer (Text - HTML)
 */
const sxw = 'application/vnd.sun.xml.writer';

/**
 * OpenOffice - Writer (Text - HTML)
 */
const sxg = 'application/vnd.sun.xml.writer.global';

/**
 * OpenOffice - Writer Template (Text - HTML)
 */
const stw = 'application/vnd.sun.xml.writer.template';

/**
 * OpenType Font File
 */
const otf = 'application/x-font-otf';

/**
 * OSFPVG
 */
const osfpvg = 'application/vnd.yamaha.openscoreformat.osfpvg+xml';

/**
 * OSGi Deployment Package
 */
const dp = 'application/vnd.osgi.dp';

/**
 * PalmOS Data
 */
const pdb = 'application/vnd.palm';

/**
 * Pascal Source File
 */
const p = 'text/x-pascal';

/**
 * PawaaFILE
 */
const paw = 'application/vnd.pawaafile';

/**
 * PCL 6 Enhanced (Formely PCL XL)
 */
const pclxl = 'application/vnd.hp-pclxl';

/**
 * Pcsel eFIF File
 */
const efif = 'application/vnd.picsel';

/**
 * PCX Image
 */
const pcx = 'image/x-pcx';

/**
 * Photoshop Document
 */
const psd = 'image/vnd.adobe.photoshop';

/**
 * PICSRules
 */
const prf = 'application/pics-rules';

/**
 * PICT Image
 */
const pic = 'image/x-pict';

/**
 * pIRCh
 */
const chat = 'application/x-chat';

/**
 * PKCS #10 - Certification Request Standard
 */
const p10 = 'application/pkcs10';

/**
 * PKCS #12 - Personal Information Exchange Syntax Standard
 */
const p12 = 'application/x-pkcs12';

/**
 * PKCS #7 - Cryptographic Message Syntax Standard
 */
const p7m = 'application/pkcs7-mime';

/**
 * PKCS #7 - Cryptographic Message Syntax Standard
 */
const p7s = 'application/pkcs7-signature';

/**
 * PKCS #7 - Cryptographic Message Syntax Standard (Certificate Request Response)
 */
const p7r = 'application/x-pkcs7-certreqresp';

/**
 * PKCS #7 - Cryptographic Message Syntax Standard (Certificates)
 */
const p7b = 'application/x-pkcs7-certificates';

/**
 * PKCS #8 - Private-Key Information Syntax Standard
 */
const p8 = 'application/pkcs8';

/**
 * PocketLearn Viewers
 */
const plf = 'application/vnd.pocketlearn';

/**
 * Portable Anymap Image
 */
const pnm = 'image/x-portable-anymap';

/**
 * Portable Bitmap Format
 */
const pbm = 'image/x-portable-bitmap';

/**
 * Portable Compiled Format
 */
const pcf = 'application/x-font-pcf';

/**
 * Portable Font Resource
 */
const pfr = 'application/font-tdpfr';

/**
 * Portable Game Notation (Chess Games)
 */
const pgn = 'application/x-chess-pgn';

/**
 * Portable Graymap Format
 */
const pgm = 'image/x-portable-graymap';

/**
 * Portable Network Graphics (PNG)
 */
const png = 'image/png';

/**
 * Portable Pixmap Format
 */
const ppm = 'image/x-portable-pixmap';

/**
 * Portable Symmetric Key Container
 */
const pskcxml = 'application/pskc+xml';

/**
 * PosML
 */
const pml = 'application/vnd.ctc-posml';

/**
 * PostScript
 */
const ai = 'application/postscript';

/**
 * PostScript Fonts
 */
const pfa = 'application/x-font-type1';

/**
 * PowerBuilder
 */
const pbd = 'application/vnd.powerbuilder6';

/**
 * Pretty Good Privacy
 */
const _pgp = 'application/pgp-encrypted';

/**
 * Pretty Good Privacy - Signature
 */
const pgp = 'application/pgp-signature';

/**
 * Preview Systems ZipLock/VBox
 */
const box = 'application/vnd.previewsystems.box';

/**
 * Princeton Video Image
 */
const ptid = 'application/vnd.pvi.ptid1';

/**
 * Pronunciation Lexicon Specification
 */
const pls = 'application/pls+xml';

/**
 * Proprietary P&G Standard Reporting System
 */
const str = 'application/vnd.pg.format';

/**
 * Proprietary P&G Standard Reporting System
 */
const ei6 = 'application/vnd.pg.osasli';

/**
 * PRS Lines Tag
 */
const dsc = 'text/prs.lines.tag';

/**
 * PSF Fonts
 */
const psf = 'application/x-font-linux-psf';

/**
 * PubliShare Objects
 */
const qps = 'application/vnd.publishare-delta-tree';

/**
 * Qualcomm's Plaza Mobile Internet
 */
const wg = 'application/vnd.pmi.widget';

/**
 * QuarkXpress
 */
const qxd = 'application/vnd.quark.quarkxpress';

/**
 * QUASS Stream Player
 */
const esf = 'application/vnd.epson.esf';

/**
 * QUASS Stream Player
 */
const msf = 'application/vnd.epson.msf';

/**
 * QUASS Stream Player
 */
const ssf = 'application/vnd.epson.ssf';

/**
 * QuickAnime Player
 */
const qam = 'application/vnd.epson.quickanime';

/**
 * Quicken
 */
const qfx = 'application/vnd.intu.qfx';

/**
 * Quicktime Video
 */
const qt = 'video/quicktime';

/**
 * RAR Archive
 */
const rar = 'application/x-rar-compressed';

/**
 * Real Audio Sound
 */
const ram = 'audio/x-pn-realaudio';

/**
 * Real Audio Sound
 */
const rmp = 'audio/x-pn-realaudio-plugin';

/**
 * Really Simple Discovery
 */
const rsd = 'application/rsd+xml';

/**
 * RealMedia
 */
const rm = 'application/vnd.rn-realmedia';

/**
 * Recordare Applications
 */
const mxl = 'application/vnd.recordare.musicxml';

/**
 * Recordare Applications
 */
const musicxml = 'application/vnd.recordare.musicxml+xml';

/**
 * Relax NG Compact Syntax
 */
const rnc = 'application/relax-ng-compact-syntax';

/**
 * RemoteDocs R-Viewer
 */
const rdz = 'application/vnd.data-vision.rdz';

/**
 * Resource Description Framework
 */
const rdf = 'application/rdf+xml';

/**
 * RetroPlatform Player
 */
const rp9 = 'application/vnd.cloanto.rp9';

/**
 * RhymBox
 */
const jisp = 'application/vnd.jisp';

/**
 * Rich Text Format
 */
const rtf = 'application/rtf';

/**
 * Rich Text Format (RTF)
 */
const rtx = 'text/richtext';

/**
 * ROUTE 66 Location Based Services
 */
const link66 = 'application/vnd.route66.link66+xml';

/**
 * RSS - Really Simple Syndication
 */
const rss = 'application/rss+xml';

/**
 * S Hexdump Format
 */
const shf = 'application/shf+xml';

/**
 * SailingTracker
 */
const st = 'application/vnd.sailingtracker.track';

/**
 * Scalable Vector Graphics (SVG)
 */
const svg = 'image/svg+xml';

/**
 * ScheduleUs
 */
const sus = 'application/vnd.sus-calendar';

/**
 * Search/Retrieve via URL Response Format
 */
const sru = 'application/sru+xml';

/**
 * Secure Electronic Transaction - Payment
 */
const setpay = 'application/set-payment-initiation';

/**
 * Secure Electronic Transaction - Registration
 */
const setreg = 'application/set-registration-initiation';

/**
 * Secured eMail
 */
const sema = 'application/vnd.sema';

/**
 * Secured eMail
 */
const semd = 'application/vnd.semd';

/**
 * Secured eMail
 */
const semf = 'application/vnd.semf';

/**
 * SeeMail
 */
const see = 'application/vnd.seemail';

/**
 * Server Normal Format
 */
const snf = 'application/x-font-snf';

/**
 * Server-Based Certificate Validation Protocol - Validation Policies - Request
 */
const spq = 'application/scvp-vp-request';

/**
 * Server-Based Certificate Validation Protocol - Validation Policies - Response
 */
const spp = 'application/scvp-vp-response';

/**
 * Server-Based Certificate Validation Protocol - Validation Request
 */
const scq = 'application/scvp-cv-request';

/**
 * Server-Based Certificate Validation Protocol - Validation Response
 */
const scs = 'application/scvp-cv-response';

/**
 * Session Description Protocol
 */
const sdp = 'application/sdp';

/**
 * Setext
 */
const etx = 'text/x-setext';

/**
 * SGI Movie
 */
const movie = 'video/x-sgi-movie';

/**
 * Shana Informed Filler
 */
const ifm = 'application/vnd.shana.informed.formdata';

/**
 * Shana Informed Filler
 */
const itp = 'application/vnd.shana.informed.formtemplate';

/**
 * Shana Informed Filler
 */
const iif = 'application/vnd.shana.informed.interchange';

/**
 * Shana Informed Filler
 */
const ipk = 'application/vnd.shana.informed.package';

/**
 * Sharing Transaction Fraud Data
 */
const tfi = 'application/thraud+xml';

/**
 * Shell Archive
 */
const shar = 'application/x-shar';

/**
 * Silicon Graphics RGB Bitmap
 */
const rgb = 'image/x-rgb';

/**
 * SimpleAnimeLite Player
 */
const slt = 'application/vnd.epson.salt';

/**
 * Simply Accounting
 */
const aso = 'application/vnd.accpac.simply.aso';

/**
 * Simply Accounting - Data Import
 */
const imp = 'application/vnd.accpac.simply.imp';

/**
 * SimTech MindMapper
 */
const twd = 'application/vnd.simtech-mindmapper';

/**
 * Sixth Floor Media - CommonSpace
 */
const csp = 'application/vnd.commonspace';

/**
 * SMAF Audio
 */
const saf = 'application/vnd.yamaha.smaf-audio';

/**
 * SMAF File
 */
const mmf = 'application/vnd.smaf';

/**
 * SMAF Phrase
 */
const spf = 'application/vnd.yamaha.smaf-phrase';

/**
 * SMART Technologies Apps
 */
const teacher = 'application/vnd.smart.teacher';

/**
 * SourceView Document
 */
const svd = 'application/vnd.svd';

/**
 * SPARQL - Query
 */
const rq = 'application/sparql-query';

/**
 * SPARQL - Results
 */
const srx = 'application/sparql-results+xml';

/**
 * Speech Recognition Grammar Specification
 */
const gram = 'application/srgs';

/**
 * Speech Recognition Grammar Specification - XML
 */
const grxml = 'application/srgs+xml';

/**
 * Speech Synthesis Markup Language
 */
const ssml = 'application/ssml+xml';

/**
 * SSEYO Koan Play File
 */
const skp = 'application/vnd.koan';

/**
 * Standard Generalized Markup Language (SGML)
 */
const sgml = 'text/sgml';

/**
 * StarOffice - Calc
 */
const sdc = 'application/vnd.stardivision.calc';

/**
 * StarOffice - Impress
 */
const sdd = 'application/vnd.stardivision.impress';

/**
 * StarOffice - Writer
 */
const sdw = 'application/vnd.stardivision.writer';

/**
 * StepMania
 */
const sm = 'application/vnd.stepmania.stepchart';

/**
 * Stuffit Archive
 */
const sit = 'application/x-stuffit';

/**
 * Stuffit Archive
 */
const sitx = 'application/x-stuffitx';

/**
 * SudokuMagic
 */
const sdkm = 'application/vnd.solent.sdkm+xml';

/**
 * Sugar Linux Application Bundle
 */
const xo = 'application/vnd.olpc-sugar';

/**
 * Sun Audio - Au file format
 */
const au = 'audio/basic';

/**
 * SundaHus WQ
 */
const wqd = 'application/vnd.wqd';

/**
 * Symbian Install Package
 */
const sis = 'application/vnd.symbian.install';

/**
 * Synchronized Multimedia Integration Language
 */
const smi = 'application/smil+xml';

/**
 * SyncML
 */
const xsm = 'application/vnd.syncml+xml';

/**
 * SyncML - Device Management
 */
const bdm = 'application/vnd.syncml.dm+wbxml';

/**
 * SyncML - Device Management
 */
const xdm = 'application/vnd.syncml.dm+xml';

/**
 * System V Release 4 CPIO Archive
 */
const sv4cpio = 'application/x-sv4cpio';

/**
 * System V Release 4 CPIO Checksum Data
 */
const sv4crc = 'application/x-sv4crc';

/**
 * Systems Biology Markup Language
 */
const sbml = 'application/sbml+xml';

/**
 * Tab Seperated Values
 */
const tsv = 'text/tab-separated-values';

/**
 * Tagged Image File Format
 */
const tiff = 'image/tiff';

/**
 * Tao Intent
 */
const tao = 'application/vnd.tao.intent-module-archive';

/**
 * Tar File (Tape Archive)
 */
const tar = 'application/x-tar';

/**
 * Tcl Script
 */
const tcl = 'application/x-tcl';

/**
 * TeX
 */
const tex = 'application/x-tex';

/**
 * TeX Font Metric
 */
const tfm = 'application/x-tex-tfm';

/**
 * Text Encoding and Interchange
 */
const tei = 'application/tei+xml';

/**
 * Text File
 */
const txt = 'text/plain';

/**
 * TIBCO Spotfire
 */
const dxp = 'application/vnd.spotfire.dxp';

/**
 * TIBCO Spotfire
 */
const sfs = 'application/vnd.spotfire.sfs';

/**
 * Time Stamped Data Envelope
 */
const tsd = 'application/timestamped-data';

/**
 * TRI Systems Config
 */
const tpt = 'application/vnd.trid.tpt';

/**
 * Triscape Map Explorer
 */
const mxs = 'application/vnd.triscape.mxs';

/**
 * troff
 */
const t = 'text/troff';

/**
 * True BASIC
 */
const tra = 'application/vnd.trueapp';

/**
 * TrueType Font
 */
const ttf = 'application/x-font-ttf';

/**
 * Turtle (Terse RDF Triple Language)
 */
const ttl = 'text/turtle';

/**
 * UMAJIN
 */
const umj = 'application/vnd.umajin';

/**
 * Unique Object Markup Language
 */
const uoml = 'application/vnd.uoml+xml';

/**
 * Unity 3d
 */
const unityweb = 'application/vnd.unity';

/**
 * Universal Forms Description Language
 */
const ufd = 'application/vnd.ufdl';

/**
 * URI Resolution Services
 */
const uri = 'text/uri-list';

/**
 * User Interface Quartz - Theme (Symbian)
 */
const utz = 'application/vnd.uiq.theme';

/**
 * Ustar (Uniform Standard Tape Archive)
 */
const ustar = 'application/x-ustar';

/**
 * UUEncode
 */
const uu = 'text/x-uuencode';

/**
 * vCalendar
 */
const vcs = 'text/x-vcalendar';

/**
 * vCard
 */
const vcf = 'text/x-vcard';

/**
 * Video CD
 */
const vcd = 'application/x-cdlink';

/**
 * Viewport+
 */
const vsf = 'application/vnd.vsf';

/**
 * Virtual Reality Modeling Language
 */
const wrl = 'model/vrml';

/**
 * VirtualCatalog
 */
const vcx = 'application/vnd.vcx';

/**
 * Virtue MTS
 */
const mts = 'model/vnd.mts';

/**
 * Virtue VTU
 */
const vtu = 'model/vnd.vtu';

/**
 * Visionary
 */
const vis = 'application/vnd.visionary';

/**
 * Vivo
 */
const viv = 'video/vnd.vivo';

/**
 * Voice Browser Call Control
 */
const ccxml = 'application/ccxml+xml,';

/**
 * VoiceXML
 */
const vxml = 'application/voicexml+xml';

/**
 * WAIS Source
 */
const src = 'application/x-wais-source';

/**
 * WAP Binary XML (WBXML)
 */
const wbxml = 'application/vnd.wap.wbxml';

/**
 * WAP Bitamp (WBMP)
 */
const wbmp = 'image/vnd.wap.wbmp';

/**
 * Waveform Audio File Format (WAV)
 */
const wav = 'audio/x-wav';

/**
 * Web Distributed Authoring and Versioning
 */
const davmount = 'application/davmount+xml';

/**
 * Web Open Font Format
 */
const woff = 'application/x-font-woff';

/**
 * Web Services Policy
 */
const wspolicy = 'application/wspolicy+xml';

/**
 * WebP Image
 */
const webp = 'image/webp';

/**
 * WebTurbo
 */
const wtb = 'application/vnd.webturbo';

/**
 * Widget Packaging and XML Configuration
 */
const wgt = 'application/widget';

/**
 * WinHelp
 */
const hlp = 'application/winhlp';

/**
 * Wireless Markup Language (WML)
 */
const wml = 'text/vnd.wap.wml';

/**
 * Wireless Markup Language Script (WMLScript)
 */
const wmls = 'text/vnd.wap.wmlscript';

/**
 * WMLScript
 */
const wmlsc = 'application/vnd.wap.wmlscriptc';

/**
 * Wordperfect
 */
const wpd = 'application/vnd.wordperfect';

/**
 * Worldtalk
 */
const stf = 'application/vnd.wt.stf';

/**
 * WSDL - Web Services Description Language
 */
const wsdl = 'application/wsdl+xml';

/**
 * X BitMap
 */
const xbm = 'image/x-xbitmap';

/**
 * X PixMap
 */
const xpm = 'image/x-xpixmap';

/**
 * X Window Dump
 */
const xwd = 'image/x-xwindowdump';

/**
 * X.509 Certificate
 */
const der = 'application/x-x509-ca-cert';

/**
 * Xfig
 */
const fig = 'application/x-xfig';

/**
 * XHTML - The Extensible HyperText Markup Language
 */
const xhtml = 'application/xhtml+xml';

/**
 * XML - Extensible Markup Language
 */
const xml = 'application/xml';

/**
 * XML Configuration Access Protocol - XCAP Diff
 */
const xdf = 'application/xcap-diff+xml';

/**
 * XML Encryption Syntax and Processing
 */
const xenc = 'application/xenc+xml';

/**
 * XML Patch Framework
 */
const xer = 'application/patch-ops-error+xml';

/**
 * XML Resource Lists
 */
const rl = 'application/resource-lists+xml';

/**
 * XML Resource Lists
 */
const rs = 'application/rls-services+xml';

/**
 * XML Resource Lists Diff
 */
const rld = 'application/resource-lists-diff+xml';

/**
 * XML Transformations
 */
const xslt = 'application/xslt+xml';

/**
 * XML-Binary Optimized Packaging
 */
const xop = 'application/xop+xml';

/**
 * XPInstall - Mozilla
 */
const xpi = 'application/x-xpinstall';

/**
 * XSPF - XML Shareable Playlist Format
 */
const xspf = 'application/xspf+xml';

/**
 * XUL - XML User Interface Language
 */
const xul = 'application/vnd.mozilla.xul+xml';

/**
 * XYZ File Format
 */
const xyz = 'chemical/x-xyz';

/**
 * YAML Ain't Markup Language / Yet Another Markup Language
 */
const yaml = 'text/yaml';

/**
 * YANG Data Modeling Language
 */
const yang = 'application/yang';

/**
 * YIN (YANG - XML)
 */
const yin = 'application/yin+xml';

/**
 * Z.U.L. Geometry
 */
const zir = 'application/vnd.zul';

/**
 * Zip Archive
 */
const zip = 'application/zip';

/**
 * ZVUE Media Manager
 */
const zmm = 'application/vnd.handheld-entertainment+xml';

/**
 * Zzazz Deck
 */
const zaz = 'application/vnd.zzazz.deck+xml';

var _types = /*#__PURE__*/Object.freeze({
	x3d: x3d,
	_3gp: _3gp,
	_3g2: _3g2,
	mseq: mseq,
	pwn: pwn,
	plb: plb,
	psb: psb,
	pvb: pvb,
	tcap: tcap,
	_7z: _7z,
	abw: abw,
	ace: ace,
	acc: acc,
	acu: acu,
	atc: atc,
	adp: adp,
	aab: aab,
	aam: aam,
	aas: aas,
	air: air,
	swf: swf,
	fxp: fxp,
	pdf: pdf,
	ppd: ppd,
	dir: dir,
	xdp: xdp,
	xfdf: xfdf,
	aac: aac,
	ahead: ahead,
	azf: azf,
	azs: azs,
	azw: azw,
	ami: ami,
	atool: atool,
	apk: apk,
	cii: cii,
	fti: fti,
	atx: atx,
	mpkg: mpkg,
	aw: aw,
	les: les,
	swi: swi,
	s: s,
	atomcat: atomcat,
	atomsvc: atomsvc,
	atom: atom,
	ac: ac,
	aif: aif,
	avi: avi,
	aep: aep,
	dxf: dxf,
	dwf: dwf,
	par: par,
	bcpio: bcpio,
	bin: bin,
	torrent: torrent,
	cod: cod,
	bmi: bmi,
	sh: sh,
	btif: btif,
	rep: rep,
	bz: bz,
	bz2: bz2,
	csh: csh,
	c: c,
	cdxml: cdxml,
	css: css,
	cdx: cdx,
	cml: cml,
	csml: csml,
	cdbcmsg: cdbcmsg,
	cla: cla,
	c4g: c4g,
	sub: sub,
	cdmia: cdmia,
	cdmic: cdmic,
	cdmid: cdmid,
	cdmio: cdmio,
	cdmiq: cdmiq,
	c11amc: c11amc,
	c11amz: c11amz,
	ras: ras,
	csv: csv,
	cpt: cpt,
	wmlc: wmlc,
	cgm: cgm,
	ice: ice,
	cmx: cmx,
	xar: xar,
	cmc: cmc,
	cpio: cpio,
	clkx: clkx,
	clkk: clkk,
	clkp: clkp,
	clkt: clkt,
	clkw: clkw,
	wbs: wbs,
	cryptonote: cryptonote,
	cif: cif,
	cmdf: cmdf,
	cu: cu,
	cww: cww,
	dcurl: dcurl,
	mcurl: mcurl,
	scurl: scurl,
	car: car,
	pcurl: pcurl,
	cmp: cmp,
	dssc: dssc,
	xdssc: xdssc,
	deb: deb,
	uva: uva,
	uvi: uvi,
	uvh: uvh,
	uvm: uvm,
	uvu: uvu,
	uvp: uvp,
	uvs: uvs,
	uvv: uvv,
	dvi: dvi,
	seed: seed,
	dtb: dtb,
	res: res,
	ait: ait,
	svc: svc,
	eol: eol,
	djvu: djvu,
	dtd: dtd,
	mlp: mlp,
	wad: wad,
	dpg: dpg,
	dra: dra,
	dfac: dfac,
	dts: dts,
	dtshd: dtshd,
	dwg: dwg,
	geo: geo,
	es: es,
	mag: mag,
	mmr: mmr,
	rlc: rlc,
	exi: exi,
	mgz: mgz,
	epub: epub,
	eml: eml,
	nml: nml,
	xpr: xpr,
	xif: xif,
	xfdl: xfdl,
	emma: emma,
	ez2: ez2,
	ez3: ez3,
	fst: fst,
	fvt: fvt,
	fbs: fbs,
	fe_launch: fe_launch,
	f4v: f4v,
	flv: flv,
	fpx: fpx,
	npx: npx,
	flx: flx,
	fli: fli,
	ftc: ftc,
	fdf: fdf,
	f: f,
	mif: mif,
	fm: fm,
	fh: fh,
	fsc: fsc,
	fnc: fnc,
	ltf: ltf,
	ddd: ddd,
	xdw: xdw,
	xbd: xbd,
	oas: oas,
	oa2: oa2,
	oa3: oa3,
	fg5: fg5,
	bh2: bh2,
	spl: spl,
	fzs: fzs,
	g3: g3,
	gmx: gmx,
	gtw: gtw,
	txd: txd,
	ggb: ggb,
	ggt: ggt,
	gdl: gdl,
	gex: gex,
	gxt: gxt,
	g2w: g2w,
	g3w: g3w,
	gsf: gsf,
	bdf: bdf,
	gtar: gtar,
	texinfo: texinfo,
	gnumeric: gnumeric,
	kml: kml,
	kmz: kmz,
	gqf: gqf,
	gif: gif,
	gv: gv,
	gac: gac,
	ghf: ghf,
	gim: gim,
	grv: grv,
	gtm: gtm,
	tpl: tpl,
	vcg: vcg,
	h261: h261,
	h263: h263,
	h264: h264,
	hpid: hpid,
	hps: hps,
	hdf: hdf,
	rip: rip,
	hbci: hbci,
	jlt: jlt,
	pcl: pcl,
	hpgl: hpgl,
	hvs: hvs,
	hvd: hvd,
	hvp: hvp,
	sfd_hdstx: sfd_hdstx,
	stk: stk,
	hal: hal,
	html: html,
	irm: irm,
	sc: sc,
	ics: ics,
	icc: icc,
	ico: ico,
	igl: igl,
	ief: ief,
	ivp: ivp,
	ivu: ivu,
	rif: rif,
	spot: spot,
	igs: igs,
	i2g: i2g,
	cdy: cdy,
	xpw: xpw,
	fcs: fcs,
	ipfix: ipfix,
	cer: cer,
	pki: pki,
	crl: crl,
	pkipath: pkipath,
	igm: igm,
	rcprofile: rcprofile,
	irp: irp,
	jad: jad,
	jar: jar,
	_class: _class,
	jnlp: jnlp,
	ser: ser,
	java: java,
	js: js,
	json: json,
	joda: joda,
	jpm: jpm,
	jpeg: jpeg,
	jpgv: jpgv,
	ktz: ktz,
	mmd: mmd,
	karbon: karbon,
	chrt: chrt,
	kfo: kfo,
	flw: flw,
	kon: kon,
	kpr: kpr,
	ksp: ksp,
	kwd: kwd,
	htke: htke,
	kia: kia,
	kne: kne,
	sse: sse,
	lasxml: lasxml,
	latex: latex,
	lbd: lbd,
	lbe: lbe,
	jam: jam,
	_123: _123,
	apr: apr,
	pre: pre,
	nsf: nsf,
	org: org,
	scm: scm,
	lwp: lwp,
	lvp: lvp,
	m3u: m3u,
	m4v: m4v,
	hqx: hqx,
	portpkg: portpkg,
	mgp: mgp,
	mrc: mrc,
	mrcx: mrcx,
	mxf: mxf,
	nbp: nbp,
	ma: ma,
	mathml: mathml,
	mbox: mbox,
	mc1: mc1,
	mscml: mscml,
	cdkey: cdkey,
	mwf: mwf,
	mfm: mfm,
	msh: msh,
	mads: mads,
	mets: mets,
	mods: mods,
	meta4: meta4,
	potm: potm,
	docm: docm,
	dotm: dotm,
	mcd: mcd,
	flo: flo,
	igx: igx,
	es3: es3,
	mdb: mdb,
	asf: asf,
	exe: exe,
	cil: cil,
	cab: cab,
	ims: ims,
	application: application,
	clp: clp,
	mdi: mdi,
	eot: eot,
	xls: xls,
	xlam: xlam,
	xlsb: xlsb,
	xltm: xltm,
	xlsm: xlsm,
	chm: chm,
	crd: crd,
	lrm: lrm,
	mvb: mvb,
	mny: mny,
	pptx: pptx,
	sldx: sldx,
	ppsx: ppsx,
	potx: potx,
	xlsx: xlsx,
	xltx: xltx,
	docx: docx,
	dotx: dotx,
	obd: obd,
	thmx: thmx,
	onetoc: onetoc,
	pya: pya,
	pyv: pyv,
	ppt: ppt,
	ppam: ppam,
	sldm: sldm,
	pptm: pptm,
	ppsm: ppsm,
	mpp: mpp,
	pub: pub,
	scd: scd,
	xap: xap,
	stl: stl,
	cat: cat,
	vsd: vsd,
	wm: wm,
	wma: wma,
	wax: wax,
	wmx: wmx,
	wmd: wmd,
	wpl: wpl,
	wmz: wmz,
	wmv: wmv,
	wvx: wvx,
	wmf: wmf,
	trm: trm,
	doc: doc,
	wri: wri,
	wps: wps,
	xbap: xbap,
	xps: xps,
	mid: mid,
	mpy: mpy,
	afp: afp,
	rms: rms,
	tmo: tmo,
	prc: prc,
	mbk: mbk,
	dis: dis,
	plc: plc,
	mqy: mqy,
	msl: msl,
	txf: txf,
	daf: daf,
	fly: fly,
	mpc: mpc,
	mpn: mpn,
	mj2: mj2,
	mpga: mpga,
	mxu: mxu,
	mpeg: mpeg,
	m21: m21,
	mp4a: mp4a,
	_mp4: _mp4,
	mp4: mp4,
	m3u8: m3u8,
	mus: mus,
	msty: msty,
	mxml: mxml,
	ngdat: ngdat,
	n_gage: n_gage,
	ncx: ncx,
	nc: nc,
	nlu: nlu,
	dna: dna,
	nnd: nnd,
	nns: nns,
	nnw: nnw,
	rpst: rpst,
	rpss: rpss,
	n3: n3,
	edm: edm,
	edx: edx,
	ext: ext,
	gph: gph,
	ecelp4800: ecelp4800,
	ecelp7470: ecelp7470,
	ecelp9600: ecelp9600,
	oda: oda,
	ogx: ogx,
	oga: oga,
	ogv: ogv,
	dd2: dd2,
	oth: oth,
	opf: opf,
	qbo: qbo,
	oxt: oxt,
	osf: osf,
	weba: weba,
	webm: webm,
	odc: odc,
	otc: otc,
	odb: odb,
	odf: odf,
	odft: odft,
	odg: odg,
	otg: otg,
	odi: odi,
	oti: oti,
	odp: odp,
	otp: otp,
	ods: ods,
	ots: ots,
	odt: odt,
	odm: odm,
	ott: ott,
	ktx: ktx,
	sxc: sxc,
	stc: stc,
	sxd: sxd,
	std: std,
	sxi: sxi,
	sti: sti,
	sxm: sxm,
	sxw: sxw,
	sxg: sxg,
	stw: stw,
	otf: otf,
	osfpvg: osfpvg,
	dp: dp,
	pdb: pdb,
	p: p,
	paw: paw,
	pclxl: pclxl,
	efif: efif,
	pcx: pcx,
	psd: psd,
	prf: prf,
	pic: pic,
	chat: chat,
	p10: p10,
	p12: p12,
	p7m: p7m,
	p7s: p7s,
	p7r: p7r,
	p7b: p7b,
	p8: p8,
	plf: plf,
	pnm: pnm,
	pbm: pbm,
	pcf: pcf,
	pfr: pfr,
	pgn: pgn,
	pgm: pgm,
	png: png,
	ppm: ppm,
	pskcxml: pskcxml,
	pml: pml,
	ai: ai,
	pfa: pfa,
	pbd: pbd,
	_pgp: _pgp,
	pgp: pgp,
	box: box,
	ptid: ptid,
	pls: pls,
	str: str,
	ei6: ei6,
	dsc: dsc,
	psf: psf,
	qps: qps,
	wg: wg,
	qxd: qxd,
	esf: esf,
	msf: msf,
	ssf: ssf,
	qam: qam,
	qfx: qfx,
	qt: qt,
	rar: rar,
	ram: ram,
	rmp: rmp,
	rsd: rsd,
	rm: rm,
	mxl: mxl,
	musicxml: musicxml,
	rnc: rnc,
	rdz: rdz,
	rdf: rdf,
	rp9: rp9,
	jisp: jisp,
	rtf: rtf,
	rtx: rtx,
	link66: link66,
	rss: rss,
	shf: shf,
	st: st,
	svg: svg,
	sus: sus,
	sru: sru,
	setpay: setpay,
	setreg: setreg,
	sema: sema,
	semd: semd,
	semf: semf,
	see: see,
	snf: snf,
	spq: spq,
	spp: spp,
	scq: scq,
	scs: scs,
	sdp: sdp,
	etx: etx,
	movie: movie,
	ifm: ifm,
	itp: itp,
	iif: iif,
	ipk: ipk,
	tfi: tfi,
	shar: shar,
	rgb: rgb,
	slt: slt,
	aso: aso,
	imp: imp,
	twd: twd,
	csp: csp,
	saf: saf,
	mmf: mmf,
	spf: spf,
	teacher: teacher,
	svd: svd,
	rq: rq,
	srx: srx,
	gram: gram,
	grxml: grxml,
	ssml: ssml,
	skp: skp,
	sgml: sgml,
	sdc: sdc,
	sdd: sdd,
	sdw: sdw,
	sm: sm,
	sit: sit,
	sitx: sitx,
	sdkm: sdkm,
	xo: xo,
	au: au,
	wqd: wqd,
	sis: sis,
	smi: smi,
	xsm: xsm,
	bdm: bdm,
	xdm: xdm,
	sv4cpio: sv4cpio,
	sv4crc: sv4crc,
	sbml: sbml,
	tsv: tsv,
	tiff: tiff,
	tao: tao,
	tar: tar,
	tcl: tcl,
	tex: tex,
	tfm: tfm,
	tei: tei,
	txt: txt,
	dxp: dxp,
	sfs: sfs,
	tsd: tsd,
	tpt: tpt,
	mxs: mxs,
	t: t,
	tra: tra,
	ttf: ttf,
	ttl: ttl,
	umj: umj,
	uoml: uoml,
	unityweb: unityweb,
	ufd: ufd,
	uri: uri,
	utz: utz,
	ustar: ustar,
	uu: uu,
	vcs: vcs,
	vcf: vcf,
	vcd: vcd,
	vsf: vsf,
	wrl: wrl,
	vcx: vcx,
	mts: mts,
	vtu: vtu,
	vis: vis,
	viv: viv,
	ccxml: ccxml,
	vxml: vxml,
	src: src,
	wbxml: wbxml,
	wbmp: wbmp,
	wav: wav,
	davmount: davmount,
	woff: woff,
	wspolicy: wspolicy,
	webp: webp,
	wtb: wtb,
	wgt: wgt,
	hlp: hlp,
	wml: wml,
	wmls: wmls,
	wmlsc: wmlsc,
	wpd: wpd,
	stf: stf,
	wsdl: wsdl,
	xbm: xbm,
	xpm: xpm,
	xwd: xwd,
	der: der,
	fig: fig,
	xhtml: xhtml,
	xml: xml,
	xdf: xdf,
	xenc: xenc,
	xer: xer,
	rl: rl,
	rs: rs,
	rld: rld,
	xslt: xslt,
	xop: xop,
	xpi: xpi,
	xspf: xspf,
	xul: xul,
	xyz: xyz,
	yaml: yaml,
	yang: yang,
	yin: yin,
	zir: zir,
	zip: zip,
	zmm: zmm,
	zaz: zaz
});

/* globals */

/**
 * @type {CONSTSCODES}
 */
const codes = _codes;

	/**
	 * @type {CONSTSHEADERS}
	 */
const headers = _headers;

/**
 * @type {CONSTSMETHODS}
 */
const methods = _methods;

/**
 * @type {CONSTSTYPES}
 */
const types = _types;

exports.codes = codes;
exports.headers = headers;
exports.methods = methods;
exports.types = types;

//# sourceMappingURL=data:application/json;charset=utf8;base64,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
